import { useEffect, useState, useRef } from 'react';
import React from 'react';
import { fetchData } from '../../../apiService';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, FormHelperText, Snackbar, Alert,
  CircularProgress, Backdrop, Autocomplete, Checkbox, Chip,
  InputAdornment, Pagination, FormControlLabel
} from '@mui/material';
import { Close, Add, Delete, ShoppingCart, Visibility, CheckCircle, Warning, List, Search, ExpandMore, ExpandLess } from '@mui/icons-material';
import { formatDate } from '../../../utils/dateUtils';

const SaleModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  saleDetails,
  setSaleDetails,
  saleBundleDetails,
  setSaleBundleDetails,
  saleStockBranchDetails,
  onSave,
  onVerify,
  customers,
  items,
  banks,
  loading,
  freightPackingGST,
  companyId,
  branchId,
  token,
  onPrint,
  initialInvoiceTypeId,
  companyData,
  onCustomerChange,
}) => {
  const isBatchWise = parseInt(companyData?.SalesMethod) === 2;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Changed from 'sm' to 'md' for better mobile detection
  const [itemsModalOpen, setItemsModalOpen] = useState(false);

  // Get Items modal state
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Branch assignment state (for multi-item / split-qty branch distribution)
  const [branchList, setBranchList] = useState([]);
  const [branchAssignMode, setBranchAssignMode] = useState(false);
  // Map of itemId -> [ { splitId, branchId, qty, stock, loading, error } ]
  const [itemBranchSplits, setItemBranchSplits] = useState({});

  // Separate Branch Split modal state
  const [branchSplitModalOpen, setBranchSplitModalOpen] = useState(false);
  // Map of saleDetail._rowKey -> [ { splitId, branchId, qty, stock, loading, error } ]
  const [splitMap, setSplitMap] = useState({});
  const [branchListLoading, setBranchListLoading] = useState(false);

  // Ledger modal state
  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerGroupData, setLedgerGroupData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerFromDate, setLedgerFromDate] = useState('');
  const [ledgerToDate, setLedgerToDate] = useState('');

  // New state for additional dropdowns
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [customerClosingBalance, setCustomerClosingBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Ref to always have latest formData without stale closure issues
  const formDataRef = useRef(formData);
  useEffect(() => { formDataRef.current = formData; }, [formData]);

  // Ref to capture initial CustomerContactNumber from prop (before any async state updates overwrite it)
  // This is needed because setFormData(prev=>{...}) in async useEffect may see stale prev
  const initialContactRef = useRef('');
  useEffect(() => {
    if (open && formData.CustomerContactNumber) {
      initialContactRef.current = formData.CustomerContactNumber;
    }
    if (!open) {
      initialContactRef.current = '';
    }
  }, [open, formData.CustomerContactNumber]);

  // Batch-wise state: map of rowKey -> { batches: [], loading: bool }
  const [batchData, setBatchData] = useState({});
  // GstCalculationType: 1 = Excluding Tax, 2 = Including Tax - REMOVED

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Cancel confirmation modal state
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Bundle items state
  const [bundleLoadingMap, setBundleLoadingMap] = useState({}); // { itemId: bool }
  const [bundleExpandedMap, setBundleExpandedMap] = useState({}); // { rowIndex: bool }

  // Hold save confirmation modal state
  const [holdConfirmOpen, setHoldConfirmOpen] = useState(false);

  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Sale' : modalAction === 'edit' ? 'Edit Sale' : 'View Sale';

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);
      setCustomerClosingBalance(null);
      setLedgerGroupData(null);
      setBatchData({});

      // Ensure all existing rows have a stable _rowKey
      setSaleDetails(prev => prev.map(d =>
        d._rowKey ? d : { ...d, _rowKey: `row_${Date.now()}_${Math.random()}` }
      ));

      // Sync customerInputValue with pre-filled CustomerName (e.g. from SO/Enquiry conversion)
      // Use formDataRef.current to avoid stale closure — setFormData and setOpenModal are batched,
      // so formData prop here may be the previous render's value
      if (formDataRef.current.CustomerName) {
        setCustomerInputValue(formDataRef.current.CustomerName);
      }

      // Fetch dropdown data and branch details without loading
      const fetchAllDropdownData = async () => {
        try {
          const [branchDetails] = await Promise.all([
            fetchBranchDetails(),
            fetchInvoiceTypes(),
            fetchStates(),
            fetchSalesmen()
          ]);

          // Auto-populate dispatch details with branch information (for all modes)
          setFormData(prev => ({
            ...prev,
            DispatchFrom: prev.DispatchFrom || branchDetails.branchName,
            DispatchLocation: prev.DispatchLocation || branchDetails.location,
            DispatchAddress1: prev.DispatchAddress1 || branchDetails.addressLine1,
            DispatchAddress2: prev.DispatchAddress2 || branchDetails.addressLine2,
            DispatchPincode: prev.DispatchPincode || branchDetails.pincode,
            TransporterName: prev.TransporterName || branchDetails.companyName,
            TransporterGST: prev.TransporterGST || branchDetails.gstNumber,
            // initialContactRef captures the prop value before async overwrite — always preserve it
            CustomerContactNumber: prev.CustomerContactNumber || initialContactRef.current || ''
          }));

          // Fetch ledger group data for edit/view mode (to show credit warnings without opening Ledger modal)
          if ((modalAction === 'edit' || modalAction === 'view') && formData.MasterId) {
            const now = new Date();
            const fyStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            const fyEnd = now.toISOString().split('T')[0];
            fetchLedgerGroupData(formData.MasterId, fyStart, fyEnd);
            fetchCustomerLedgerDetails(formData.MasterId);
            // Fetch contact number for edit/view mode if not already present
            if (!formData.CustomerContactNumber) {
              fetchCustomerDetails(formData.MasterId).then(customerDetails => {
                if (customerDetails.contactNumber) {
                  setFormData(prev => ({
                    ...prev,
                    CustomerContactNumber: customerDetails.contactNumber
                  }));
                }
              });
            }
          }

          // For create mode with a pre-filled MasterId (e.g. from Order/SO conversion),
          // fetch contact number if not already populated.
          // Use formDataRef.current to avoid stale closure (formData captured at render time may be empty)
          if (modalAction === 'create' && formDataRef.current.MasterId && !formDataRef.current.CustomerContactNumber) {
            fetchCustomerDetails(formDataRef.current.MasterId).then(customerDetails => {
              if (customerDetails.contactNumber) {
                setFormData(prev => ({
                  ...prev,
                  CustomerContactNumber: prev.CustomerContactNumber || customerDetails.contactNumber
                }));
              }
            });
          }

          // Fetch bill number in create mode ONLY if not already pre-filled
          if (modalAction === 'create') {
            // Small delay to let React commit the latest formData state before checking
            setTimeout(() => {
              const currentBillNumber = formDataRef.current.BillNumber;
              if (currentBillNumber === '' || currentBillNumber === null || currentBillNumber === undefined) {
                const invoiceTypeId = initialInvoiceTypeId || formDataRef.current.InvoiceTypeId || null;
                const endpoint = invoiceTypeId
                  ? `/MaxBillno/${companyId}/${branchId}/${invoiceTypeId}`
                  : `/MaxBillno/${companyId}/${branchId}`;
                fetchData('SaleApi', endpoint, token)
                  .then(res => {
                    const d = res.response_data || res || {};
                    const bn = d.BillNumber ?? d.billNumber ?? '';
                    const bp = d.BillPrefix || d.billPrefix || '';
                    const fy = d.FinYear || d.finYear || '';
                    setFormData(prev => ({
                      ...prev,
                      BillNumber: (prev.BillNumber !== '' && prev.BillNumber !== null && prev.BillNumber !== undefined) ? prev.BillNumber : bn,
                      BillPrefix: prev.BillPrefix || bp,
                      FinYear: prev.FinYear || fy,
                    }));
                  })
                  .catch(e => console.error('Error fetching bill number:', e));
              }
            }, 50);
          }
        } catch (error) {
          console.error('Error fetching dropdown data:', error);
        }
      };

      fetchAllDropdownData();
    }
  }, [open, modalAction]);

  // Fetch Invoice Types
  const fetchInvoiceTypes = async () => {
    try {
      const response = await fetchData('InvoiceTypeApi', `/${companyId}`, token);
      setInvoiceTypes(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching invoice types:', error);
      setInvoiceTypes([]);
    }
  };

  // Fetch States
  const fetchStates = async () => {
    try {
      const response = await fetchData('SaleApi', `/StateCode/${companyId}/${branchId}`, token);
      setStates(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    }
  };

  // Fetch Salesmen
  const fetchSalesmen = async () => {
    try {
      const response = await fetchData('SalesExecutiveApi', `/${companyId}`, token);
      setSalesmen(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching salesmen:', error);
      setSalesmen([]);
    }
  };

  // Fetch branch details for dispatch section
  const fetchBranchDetails = async () => {
    try {
      const response = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      if (response.responseCode == '1111') {
        const branchData = response.response_data || {};
        return {
          branchName: branchData.Name || '',
          location: branchData.Location || '',
          addressLine1: branchData.AddressLine1 || '',
          addressLine2: branchData.AddressLine2 || '',
          pincode: branchData.PinCode || '',
          companyName: branchData.CompanyName || branchData.Name || '',
          gstNumber: branchData.GSTNumber || branchData.CompanyGSTNumber || ''
        };
      }
      return { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '' };
    } catch (error) {
      console.error('Error fetching Branch Details:', error);
      return { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '' };
    }
  };

  // Fetch customer details for shipping section using ClientApi
  const fetchSingleClient = async (id) => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${id}`, token);
      return response.response_data.Ledger || response || null;
    } catch (error) {
      console.error('Error fetching client:', error);
      return null;
    }
  };

  // Fetch customer ledger details for closing balance
  const fetchCustomerLedgerDetails = async (masterId) => {
    if (!masterId) {
      setCustomerClosingBalance(null);
      return;
    }

    setLoadingBalance(true);
    try {
      const fromDate = formData.BillDate || new Date().toISOString().split('T')[0];
      const toDate = new Date().toISOString().split('T')[0];

      const response = await fetchData('SaleApi', `/MasterLedgerDetails/${companyId}/${masterId}/${fromDate}/${toDate}`, token);
      const responseData = response.response_data || response || [];

      // Handle array response - get the first item's Amount
      let closingBalance = 0;
      if (Array.isArray(responseData) && responseData.length > 0) {
        closingBalance = parseFloat(responseData[0].Amount) || 0;
      } else if (responseData.Amount !== undefined) {
        closingBalance = parseFloat(responseData.Amount) || 0;
      }

      setCustomerClosingBalance(closingBalance);
    } catch (error) {
      console.error('Error fetching customer ledger details:', error);
      setCustomerClosingBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  // Fetch customer details for shipping section
  const fetchCustomerDetails = async (masterId) => {
    try {
      const customerData = await fetchSingleClient(masterId);
      if (customerData) {
        return {
          customerName: customerData.CustomerName || customerData.Name || '',
          gstNumber: customerData.GSTNumber || '',
          addressLine1: customerData.AddressLine1 || '',
          addressLine2: customerData.AddressLine2 || '',
          location: customerData.Location || '',
          pincode: customerData.PinCode || '',
          contactNumber: customerData.ContactNumber1 || customerData.ContactNumber || customerData.Mobile || ''
        };
      }
      return { customerName: '', gstNumber: '', addressLine1: '', addressLine2: '', location: '', pincode: '', contactNumber: '' };
    } catch (error) {
      console.error('Error fetching customer details:', error);
      return { customerName: '', gstNumber: '', addressLine1: '', addressLine2: '', location: '', pincode: '', contactNumber: '' };
    }
  };

  // Fetch bill number when customer changes
  const fetchBillNumberForCustomer = async (customerInvoiceTypeId) => {
    if (!customerInvoiceTypeId) return;
    if (modalAction !== 'create') return;

    try {
      const response = await fetchData('SaleApi', `/MaxBillno/${companyId}/${branchId}/${customerInvoiceTypeId}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';

      setFormData(prev => ({
        ...prev,
        BillNumber: billNumber,
        BillPrefix: billPrefix,
        FinYear: finYear
      }));
    } catch (error) {
      console.error('Error fetching bill number for customer invoice type:', error);
    }
  };

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  // Fetch batches for a specific item (batch-wise mode or HasWarranty items)
  const fetchItemBatches = async (itemId, rowKey) => {
    if (!itemId) return;
    if (!rowKey) {
      console.warn('fetchItemBatches: missing rowKey');
      return;
    }
    const masterId = parseInt(formDataRef.current.MasterId) || 0;
    const url = `/ItemBatchStockList/ItemBatch/${companyId}/${branchId}/${itemId}/${masterId}`;
    console.log('fetchItemBatches URL:', url, 'rowKey:', rowKey);
    setBatchData(prev => ({ ...prev, [rowKey]: { batches: prev[rowKey]?.batches || [], loading: true } }));
    try {
      const response = await fetchData('SaleApi', url, token);
      console.log('fetchItemBatches response:', response);
      const batches = response?.response_data || (Array.isArray(response) ? response : []);
      setBatchData(prev => ({ ...prev, [rowKey]: { batches: batches, loading: false } }));
    } catch (error) {
      console.error('Error fetching item batches:', error);
      setBatchData(prev => ({ ...prev, [rowKey]: { batches: [], loading: false } }));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Fetch bundle items for a bundle product
  // API returns ItemBundleRelation: { Id, ProductId, ItemId, Quantity, ItemName, ProductName, IsActive, BalanceStock }
  const fetchBundleItems = async (itemId, rowIndex) => {
    if (!itemId) return;

    // In edit/view mode, skip re-fetching if bundle items are already loaded from API
    if ((modalAction === 'edit' || modalAction === 'view')) {
      const existingBundles = (saleBundleDetails || []).filter(b => b._parentRowIndex === rowIndex);
      if (existingBundles.length > 0) {
        setBundleExpandedMap(prev => ({ ...prev, [rowIndex]: true }));
        return;
      }
    }

    setBundleLoadingMap(prev => ({ ...prev, [itemId]: true }));
    try {
      const response = await fetchData('SaleApi', `/ItemBundleDetails/${companyId}/${branchId}/${itemId}`, token);
      const bundleData = response?.response_data || (Array.isArray(response) ? response : []);

      // Build bundle detail rows for this parent item (keyed by parent row index)
      const newBundleRows = bundleData.map(b => ({
        _parentRowIndex: rowIndex,
        _parentItemId: parseInt(itemId),
        ItemId: b.ProductId || null,        // Parent bundle product ID (swapped)
        ProductId: b.ItemId || null,        // Sub-item's own ID (swapped)
        ProductItemId: b.ProductId || null, // kept for backward compat
        ItemName: b.ItemName || '',
        ProductName: b.ProductName || '',
        Quantity: parseFloat(b.Quantity) || 0,
        Rate: parseFloat(b.Rate) || 0,
        Amount: parseFloat(b.Amount) || (parseFloat(b.Quantity) * parseFloat(b.Rate)) || 0,
        BalanceStock: b.BalanceStock != null ? parseFloat(b.BalanceStock) : null,
        IsActive: b.IsActive !== undefined ? b.IsActive : true,
        CreatedBy: b.CreatedBy || null,
        ModifiedBy: b.ModifiedBy || null,
        CreatedDate: b.CreatedDate || null,
        ModifiedDate: b.ModifiedDate || null,
      }));

      // Replace any existing bundle rows for this parent row, then add new ones
      setSaleBundleDetails(prev => {
        const filtered = (prev || []).filter(b => b._parentRowIndex !== rowIndex);
        return [...filtered, ...newBundleRows];
      });
      // Auto-expand
      setBundleExpandedMap(prev => ({ ...prev, [rowIndex]: true }));
    } catch (error) {
      console.error('Error fetching bundle items:', error);
    } finally {
      setBundleLoadingMap(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Handle quantity change for bundle sub-item rows
  const handleBundleQtyChange = (parentRowIndex, bi, value) => {
    if (isViewMode) return;
    const validNumberRegex = /^\d*\.?\d*$/;
    if (!validNumberRegex.test(value)) return;
    const numValue = parseFloat(value) || 0;
    if (numValue < 0) return;

    setSaleBundleDetails(prev => {
      const updated = [...(prev || [])];
      // Find the bi-th bundle row belonging to parentRowIndex
      let count = -1;
      const globalIdx = updated.findIndex(b => {
        if (b._parentRowIndex === parentRowIndex) count++;
        return b._parentRowIndex === parentRowIndex && count === bi;
      });
      if (globalIdx === -1) return prev;
      const row = updated[globalIdx];
      const rate = parseFloat(row.Rate) || 0;
      const amount = numValue * rate;
      updated[globalIdx] = { ...row, Quantity: value === '' ? '' : numValue, Amount: amount };
      return updated;
    });
  };
  // Handle cancel with confirmation
  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  // Validation rules
  const validateField = (field, value) => {
    switch (field) {
      case 'BillNumber':
        if (!value || value.toString().trim() === '') return 'Bill Number is required';
        return '';
      case 'BillDate':
        if (!value) return 'Bill Date is required';
        return '';
      case 'MasterId':
        // Check for either MasterId or CustomerName
        if (!formData.MasterId && (!formData.CustomerName || formData.CustomerName.trim() === '')) {
          return 'Customer is required';
        }
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    const billNumberError = validateField('BillNumber', formData.BillNumber);
    if (billNumberError) newErrors.BillNumber = billNumberError;

    const billDateError = validateField('BillDate', formData.BillDate);
    if (billDateError) newErrors.BillDate = billDateError;

    // Customer validation - check for CustomerName instead of MasterId
    if (!formData.CustomerName && !formData.MasterId) {
      newErrors.MasterId = 'Customer is required';
    }

    // Sale details validation
    if (saleDetails.length === 0) {
      newErrors.saleDetails = 'At least one item is required';
    } else {
      const detailErrors = [];
      saleDetails.forEach((detail, index) => {
        const itemErrors = {};
        if (!detail.Quantity || parseFloat(detail.Quantity) <= 0) {
          itemErrors.Quantity = 'Quantity must be greater than 0';
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.Rate = 'Rate must be greater than 0';
        }
        if (Object.keys(itemErrors).length > 0) {
          detailErrors[index] = itemErrors;
        }
      });
      if (detailErrors.length > 0) {
        newErrors.details = detailErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur for touched state
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));

    if (field === 'MasterId') {
      // Custom validation for customer field
      const error = (!formData.MasterId && (!formData.CustomerName || formData.CustomerName.trim() === ''))
        ? 'Customer is required'
        : '';
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      const error = validateField(field, formData[field]);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  // Check stock for all sale details — returns array of error strings
  // Aggregates qty per item+branch to validate total doesn't exceed stock
  const getStockErrors = () => {
    const stockErrors = [];
    // Group by item + branch to check aggregate stock
    const itemBranchMap = {}; // key: `${itemId}_${branchId}` => { totalQty, stock, itemName, rows, hasBranchSplits, companyStock }
    
    saleDetails.forEach((detail, index) => {
      if (detail.HasWarranty) return; // batch-level stock validated separately
      if (!detail.ItemId) return; // skip empty rows
      const qty = parseFloat(detail.Quantity) || 0;
      if (qty <= 0) return; // skip rows with no qty

      // If this row has branch splits, stock validation is handled per-split — skip login-branch check
      if (detail._branchSplits && detail._branchSplits.length > 0) return;
      
      const detailBranchId = parseInt(detail.AssignedBranchId) || parseInt(branchId) || 0;
      const key = `${detail.ItemId}_${detailBranchId}`;
      
      if (!itemBranchMap[key]) {
        const itemMeta = items.find(i => i.Id === parseInt(detail.ItemId));
        // Service items don't carry physical stock — skip stock validation entirely
        if (itemMeta?.IsService) return;
        const rawStock = detail.BalanceStock !== undefined
          ? parseFloat(detail.BalanceStock) || 0
          : parseFloat(itemMeta?.CurrentBranchStock ?? itemMeta?.BalanceStock) || 0;
        // In edit mode, restore the previously sold quantity back to available stock
        const prevQty = modalAction === 'edit' ? (parseFloat(detail.PrevQuantity) || 0) : 0;
        const stock = rawStock + prevQty;
        const companyStock = parseFloat(itemMeta?.CompanyStock) || 0;
        itemBranchMap[key] = { totalQty: 0, stock, itemName: detail.ItemName || 'Item', rows: [], companyStock };
      }
      itemBranchMap[key].totalQty += qty;
      itemBranchMap[key].rows.push(index + 1);
    });

    // Check each group
    Object.values(itemBranchMap).forEach(({ totalQty, stock, itemName, rows, companyStock }) => {
      if (totalQty > stock) {
        // If login branch has no stock but other branches do, branch split is required
        if (stock === 0 && companyStock > 0) {
          const rowLabel = rows.length > 1 ? `Rows ${rows.join(', ')}` : `Row ${rows[0]}`;
          stockErrors.push(`${rowLabel} (${itemName}): this branch has no stock — please assign Branch Split to allocate from another branch`);
          return;
        }
        const rowLabel = rows.length > 1 ? `Rows ${rows.join(', ')}` : `Row ${rows[0]}`;
        stockErrors.push(`${rowLabel} (${itemName}): total qty (${totalQty}) exceeds available stock (${stock})`);
      }
    });

    return stockErrors;
  };

  // Handle save with validation
  const handleSaveWithValidation = () => {
    // Mark all fields as touched
    setTouched({ BillNumber: true, BillDate: true, MasterId: true });

    // Build validation error messages
    const validationMessages = [];

    if (!formData.BillNumber || formData.BillNumber.toString().trim() === '') {
      validationMessages.push('Bill Number is required');
    }
    if (!formData.BillDate) {
      validationMessages.push('Bill Date is required');
    }
    if (!formData.CustomerName && !formData.MasterId) {
      validationMessages.push('Customer is required');
    }
    if (saleDetails.length === 0) {
      validationMessages.push('At least one item is required');
    } else {
      // Check each item for Received Qty and Rate
      const itemErrors = [];
      saleDetails.forEach((detail, index) => {
        if (!detail.Quantity || parseFloat(detail.Quantity) <= 0) {
          itemErrors.push(`Row ${index + 1}: Received Qty is required`);
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.push(`Row ${index + 1}: Rate is required`);
        }
      });
      if (itemErrors.length > 0) {
        validationMessages.push(...itemErrors);
      } else {
        // Stock validation — block save/hold if insufficient stock
        validationMessages.push(...getStockErrors());
      }
    }

    // Validate payment amounts
    const totalAmount = parseFloat(formData.TotalAmount) || 0;
    const cashAmount = parseFloat(formData.CashAmount) || 0;
    const upiAmount = parseFloat(formData.UPIAmount) || 0;
    const cardAmount = parseFloat(formData.CardAmount) || 0;
    const totalPayments = cashAmount + upiAmount + cardAmount;

    if (totalPayments > totalAmount) {
      validationMessages.push(`Total payment amount (₹${totalPayments.toFixed(2)}) cannot exceed total amount (₹${totalAmount.toFixed(2)})`);
    }

    // Validate Exchange Amount does not exceed Total Amount (before exchange deduction)
    if (formData.IsExchange) {
      const exchangeAmt = parseFloat(formData.ExchangeAmount) || 0;
      // Gross total = current TotalAmount + exchangeAmt (since exchange was already deducted from TotalAmount)
      const grossTotal = totalAmount + exchangeAmt;
      if (exchangeAmt > grossTotal) {
        validationMessages.push(`Exchange Amount (₹${exchangeAmt.toFixed(2)}) cannot exceed Total Amount (₹${grossTotal.toFixed(2)})`);
      }
    }

    // Validate bank selection if UPI or Card amount is entered
    if ((upiAmount > 0 || cardAmount > 0) && !formData.BankId) {
      validationMessages.push('Bank selection is required when UPI or Card payment is made');
    }

    if (validationMessages.length > 0) {
      showError(validationMessages.join(' | '));
      validateForm();
      return;
    }

    if (validateForm()) {
      // If sale is on hold, show confirmation dialog before saving
      if (formData.IsHold) {
        setHoldConfirmOpen(true);
        return;
      }
      onSave();
    }
  };

  // Sync gstType with formData - REMOVED
  // useEffect(() => {
  //   if (formData.GstCalculationType) {
  //     setGstType(formData.GstCalculationType);
  //   }
  // }, [formData.GstCalculationType]);

  // const handleGstTypeChange = (value) => {
  //   const intValue = parseInt(value);
  //   setGstType(intValue);
  //   const gstTypeName = intValue === 2 ? 'Including Tax' : 'Excluding Tax';
  //   setFormData(prev => ({ ...prev, GstCalculationType: intValue, GstCalculationTypeName: gstTypeName }));
  // };

  const handleChange = (field, value) => {
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    // When Installation checkbox is ticked, default InstallationDate to current date + 1
    if (field === 'IsInstallation' && value === true) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        IsInstallation: true,
        InstallationDate: prev.InstallationDate || tomorrowStr,
      }));
      return;
    }

    // Installation Date validation: must be >= today and within current year only
    if (field === 'InstallationDate') {
      const today = new Date().toISOString().split('T')[0];
      const currentYear = new Date().getFullYear();
      const selectedYear = value ? new Date(value).getFullYear() : null;

      if (value < today) {
        setErrors(prev => ({ ...prev, InstallationDate: 'Installation Date must be today or a future date' }));
        return;
      }
      if (selectedYear && selectedYear !== currentYear) {
        setErrors(prev => ({ ...prev, InstallationDate: `Installation Date must be within the current year (${currentYear})` }));
        return;
      }
      setErrors(prev => ({ ...prev, InstallationDate: '' }));
    }

    if (field === 'CustomerName') {
      // Handle customer name change (from Autocomplete)
      const selectedCustomer = customers.find(s => s.Name === value);
      if (selectedCustomer) {
        // Existing customer selected
        const invoiceTypeName = selectedCustomer.InvoiceTypeName || selectedCustomer.InvoiceType || 'B2B';
        const gstNumber = selectedCustomer.GSTNumber || selectedCustomer.GSTNo || '';
        const masterId = selectedCustomer.MasterId || selectedCustomer.Id;

        // Find the invoice type ID from the invoice types list
        const invoiceType = invoiceTypes.find(it => it.Name === invoiceTypeName);

        setFormData(prev => ({
          ...prev,
          MasterId: masterId,
          CustomerName: value,
          InvoiceTypeName: invoiceTypeName,
          // In edit mode, preserve existing InvoiceTypeId and StateId
          InvoiceTypeId: modalAction === 'edit' ? (prev.InvoiceTypeId ?? invoiceType?.Id ?? null) : (invoiceType?.Id || prev.InvoiceTypeId || null),
          GSTNumber: gstNumber,
          StateId: modalAction === 'edit' ? prev.StateId : (invoiceTypeName.toLowerCase().includes('igst') ? prev.StateId : null),
          // Auto-map customer's SalesExecutive to SalesMan dropdown
          SalesManId: selectedCustomer.SalesExecutiveId || selectedCustomer.ExecutiveId || prev.SalesManId || null
        }));

        // Fetch customer closing balance and group ledger data
        fetchCustomerLedgerDetails(masterId);
        // Use current month range - same as ledger modal - to get accurate MaxPendingDays
        const today2 = new Date();
        const fyStart = new Date(today2.getFullYear(), today2.getMonth(), 1).toISOString().split('T')[0];
        const fyEnd = today2.toISOString().split('T')[0];
        fetchLedgerGroupData(masterId, fyStart, fyEnd);

        // Notify parent to refresh items for this customer
        if (onCustomerChange) onCustomerChange(masterId);

        // Fetch bill number based on customer's invoice type
        if (modalAction === 'create' && invoiceType?.Id) {
          fetchBillNumberForCustomer(invoiceType.Id);
        }

        // Fetch and populate customer details for shipping section
        // In edit/view mode, only fill fields that are currently empty to avoid
        // overwriting saved values loaded from the API
        if (masterId) {
          fetchCustomerDetails(masterId).then(customerDetails => {
            setFormData(prev => {
              const isEditOrView = modalAction === 'edit' || modalAction === 'view';
              return {
                ...prev,
                ShipTo: isEditOrView ? (prev.ShipTo || customerDetails.customerName || value) : (customerDetails.customerName || value),
                ShipGSTNo: isEditOrView ? (prev.ShipGSTNo || customerDetails.gstNumber) : customerDetails.gstNumber,
                ShipToAddress1: isEditOrView ? (prev.ShipToAddress1 || customerDetails.addressLine1) : customerDetails.addressLine1,
                ShipToAddress2: isEditOrView ? (prev.ShipToAddress2 || customerDetails.addressLine2) : customerDetails.addressLine2,
                ShipToLocation: isEditOrView ? (prev.ShipToLocation || customerDetails.location) : customerDetails.location,
                ShipToPincode: isEditOrView ? (prev.ShipToPincode || customerDetails.pincode) : customerDetails.pincode,
                CustomerContactNumber: isEditOrView ? (prev.CustomerContactNumber || customerDetails.contactNumber) : customerDetails.contactNumber,
              };
            });
          });
        }
      } else {
        // New customer name typed
        const defaultB2CInvoiceType = invoiceTypes.find(it =>
          it.Name.toLowerCase().includes('b to c') || it.Name.toLowerCase().includes('b2c')
        );
        setFormData(prev => ({
          ...prev,
          MasterId: null, // No MasterId for new customer
          CustomerName: value,
          InvoiceTypeName: defaultB2CInvoiceType?.Name || 'B to C', // Default to B to C for new customers
          InvoiceTypeId: defaultB2CInvoiceType?.Id || null,
          GSTNumber: '',
          StateId: null,
          // Set shipping details for new customers
          ShipTo: value, // Use the typed customer name
          ShipGSTNo: '',
          ShipToAddress1: '',
          ShipToAddress2: '',
          ShipToLocation: '',
          ShipToPincode: '',
          CustomerContactNumber: ''
          // Transporter details remain as company info from branch API
        }));

        // Clear closing balance and group data for new customers
        setCustomerClosingBalance(null);
        setLedgerGroupData(null);

        // Fetch bill number based on default B2C invoice type for new customers
        if (modalAction === 'create' && defaultB2CInvoiceType?.Id) {
          fetchBillNumberForCustomer(defaultB2CInvoiceType.Id);
        }
      }

      // Clear customer validation error when typing
      if (errors.MasterId && value && value.trim() !== '') {
        setErrors(prev => ({ ...prev, MasterId: '' }));
      }
    } else if (field === 'InvoiceTypeId') {
      const selectedInvoiceType = invoiceTypes.find(it => it.Id === parseInt(value));
      const invoiceTypeName = selectedInvoiceType?.Name || '';
      setFormData(prev => ({
        ...prev,
        [field]: value,
        InvoiceTypeName: invoiceTypeName,
        // Clear state if not IGST
        StateId: invoiceTypeName.toLowerCase().includes('igst') ? prev.StateId : null
      }));

      // Fetch new bill number for this invoice type (for new customers only)
      if (modalAction === 'create' && value && !formData.MasterId) {
        fetchBillNumberForCustomer(parseInt(value));
      }
    } else if (field === 'GSTNumber') {
      // Handle GST number change for new customers
      setFormData(prev => ({ ...prev, [field]: value }));

      // If this is a new customer and GST number is entered/cleared, 
      // we might need to update invoice type availability
      if (!formData.MasterId) {
        const hasGST = value && value.trim() !== '';
        const currentInvoiceType = invoiceTypes.find(it => it.Id === formData.InvoiceTypeId);

        // If current invoice type is B to B but GST is cleared, reset to B to C
        if (!hasGST && currentInvoiceType && (currentInvoiceType.Name.toLowerCase().includes('b to b') || currentInvoiceType.Name.toLowerCase().includes('b2b'))) {
          const defaultB2CInvoiceType = invoiceTypes.find(it =>
            it.Name.toLowerCase().includes('b to c') || it.Name.toLowerCase().includes('b2c')
          );
          setFormData(prev => ({
            ...prev,
            [field]: value,
            InvoiceTypeName: defaultB2CInvoiceType?.Name || 'B to C',
            InvoiceTypeId: defaultB2CInvoiceType?.Id || null
          }));
        } else {
          setFormData(prev => ({ ...prev, [field]: value }));
        }
      }
    } else if (field === 'ExchangeAmount') {
      const newValue = parseFloat(value) || 0;
      if (newValue < 0) {
        showError('Exchange Amount cannot be negative');
        return;
      }
      // Calculate the gross total before exchange deduction
      const baseTotal = (parseFloat(formData.TotalAmount) || 0) + (parseFloat(formData.ExchangeAmount) || 0);
      if (newValue > baseTotal) {
        showError(`Exchange Amount (₹${newValue.toFixed(2)}) cannot exceed Total Amount (₹${baseTotal.toFixed(2)})`);
        return;
      }
      setFormData(prev => ({ ...prev, [field]: newValue }));
    } else if (['CashAmount', 'UPIAmount', 'CardAmount'].includes(field)) {
      // Handle payment amount changes - CreditAmount is auto-calculated
      const newValue = parseFloat(value) || 0;
      const totalAmount = parseFloat(formData.TotalAmount) || 0;

      // Validate: individual amount cannot be negative
      if (newValue < 0) {
        showError(`${field.replace('Amount', '')} Amount cannot be negative`);
        return;
      }

      // Get current payment amounts
      const currentCash = field === 'CashAmount' ? newValue : (parseFloat(formData.CashAmount) || 0);
      const currentUPI = field === 'UPIAmount' ? newValue : (parseFloat(formData.UPIAmount) || 0);
      const currentCard = field === 'CardAmount' ? newValue : (parseFloat(formData.CardAmount) || 0);

      // Calculate total payments (Cash + UPI + Card)
      const totalPayments = currentCash + currentUPI + currentCard;

      // Validate total payments don't exceed total amount
      if (totalPayments > totalAmount) {
        showError(`Total payment amount (₹${totalPayments.toFixed(2)}) cannot exceed Grand Total (₹${totalAmount.toFixed(2)})`);
        return;
      }

      // CreditAmount = GrandTotal - CashAmount - CardAmount - UPIAmount
      const creditAmount = Math.max(0, parseFloat((totalAmount - totalPayments).toFixed(2)));
      const paidAmount = totalPayments;
      const balanceAmount = Math.max(0, creditAmount);

      setFormData(prev => ({
        ...prev,
        [field]: newValue,
        CreditAmount: creditAmount,
        PaidAmount: paidAmount,
        BalanceAmount: balanceAmount
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const isIGSTInvoice = () => {
    const invoiceType = (formData.InvoiceTypeName || '').toLowerCase();
    return invoiceType.includes('igst');
  };

  const isIncludingTax = () => parseInt(formData.GstCalculationType) === 2;

  const handleGstTypeChange = (value) => {
    const intValue = parseInt(value);
    const gstTypeName = intValue === 2 ? 'Including Tax' : 'Excluding Tax';
    setFormData(prev => ({ ...prev, GstCalculationType: intValue, GstCalculationTypeName: gstTypeName }));
  };

  // Helper function to calculate maximum discount amount
  const getMaxDiscountAmount = (quantity, rate, gstRate) => {
    const qty = parseFloat(quantity) || 0;
    const rt = parseFloat(rate) || 0;
    const gst = parseFloat(gstRate) || 0;

    if (isIncludingTax()) {
      // For inclusive tax, discount is applied on base amount before GST
      const totalWithGst = qty * rt;
      return totalWithGst / (1 + gst / 100);
    } else {
      // For exclusive tax, discount is applied on amount
      return qty * rt;
    }
  };

  const handleDetailChange = (index, field, value) => {
    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    const updatedDetails = [...saleDetails];

    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: field.includes('Discount') ? 0 : '' };
        setSaleDetails(updatedDetails);
        calculateTotals(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;

      // Validate Rate against MinRate/MaxRate if set
      if (field === 'Rate') {
        const currentDetail = updatedDetails[index];
        const minRate = parseFloat(currentDetail.MinRate) || 0;
        const maxRate = parseFloat(currentDetail.MaxRate) || 0;
        if (maxRate > 0 && numValue > maxRate) {
          showError(`Rate (${numValue}) cannot exceed Max Rate (${maxRate})`);
          return;
        }
        if (minRate > 0 && numValue < minRate) {
          showError(`Rate (${numValue}) cannot be less than Min Rate (${minRate})`);
          return;
        }
      }

      // Validate discount percentage not above 100%
      if (field === 'DiscountPercentage') {
        if (numValue > 100) return;
      }

      // Stock validation for quantity
      if (field === 'Quantity') {
        const currentDetail = updatedDetails[index];
        const itemData = items.find(i => i.Id === parseInt(currentDetail.ItemId));
        // In edit mode, the previously saved qty is already deducted from stock — add it back
        const prevQty = modalAction === 'edit' ? (parseFloat(currentDetail.PrevQuantity) || 0) : 0;
        // In batch-wise mode or HasWarranty items, validate against selected batch's balance stock (sum all rows with same batch)
        let balanceStock;
        if ((isBatchWise || currentDetail.HasWarranty) && currentDetail.BatchNumber) {
          const rowKey = currentDetail._rowKey;
          const batches = batchData[rowKey]?.batches || [];
          const selectedBatch = batches.find(b => b.Name === currentDetail.BatchNumber);
          balanceStock = parseFloat(selectedBatch?.BalanceStock ?? selectedBatch?.BalanceQuantity ?? itemData?.CurrentBranchStock ?? itemData?.BalanceStock) || 0;
          balanceStock += prevQty; // restore previously sold qty in edit mode
          // Sum qty of other rows using the same item AND same batch
          const otherBatchQty = saleDetails.reduce((sum, d, i) => {
            if (i !== index && d.ItemId === currentDetail.ItemId && d.BatchNumber === currentDetail.BatchNumber) {
              return sum + (parseFloat(d.Quantity) || 0);
            }
            return sum;
          }, 0);
          if ((numValue + otherBatchQty) > balanceStock) {
            showError(`Total quantity (${numValue + otherBatchQty}) exceeds batch stock (${balanceStock}) for [Batch: ${currentDetail.BatchNumber}]`);
            return;
          }
        } else {
          balanceStock = parseFloat(itemData?.CurrentBranchStock ?? itemData?.BalanceStock) || 0;
          balanceStock += prevQty; // restore previously sold qty in edit mode
          // Sum qty of other rows using the same item AND same assigned branch
          const currentBranchId = parseInt(currentDetail.AssignedBranchId) || parseInt(branchId) || 0;
          const otherSameBranchQty = saleDetails.reduce((sum, d, i) => {
            if (i !== index && parseInt(d.ItemId) === parseInt(currentDetail.ItemId)) {
              const dBranchId = parseInt(d.AssignedBranchId) || parseInt(branchId) || 0;
              if (dBranchId === currentBranchId) {
                return sum + (parseFloat(d.Quantity) || 0);
              }
            }
            return sum;
          }, 0);
          if ((numValue + otherSameBranchQty) > balanceStock) {
            // If login branch has no stock but company/other branches do, allow — branch split handles it
            const companyStock = parseFloat(itemData?.CompanyStock) || 0;
            if (balanceStock === 0 && companyStock > 0) {
              // Allow — item has stock in other branches, branch split will allocate
            } else {
              showError(`Total quantity (${numValue + otherSameBranchQty}) exceeds available stock (${balanceStock}) for ${itemData?.Name || 'this item'}`);
              return;
            }
          }
        }
      }

      // Validate discount amount doesn't exceed maximum allowed (100% of item amount)
      if (field === 'DiscountAmount') {
        const currentDetail = updatedDetails[index];
        const receivedQty = Math.max(0, parseFloat(currentDetail.Quantity) || 0);
        const rate = Math.max(0, parseFloat(currentDetail.Rate) || 0);
        const gstRate = Math.max(0, parseFloat(currentDetail.GSTRate) || 0);

        let maxDiscountAmount;
        if (isIncludingTax()) {
          // For inclusive tax, discount is applied on base amount before GST
          const totalWithGst = receivedQty * rate;
          const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
          maxDiscountAmount = baseAmountBeforeGST;
        } else {
          // For exclusive tax, discount is applied on amount
          maxDiscountAmount = receivedQty * rate;
        }

        if (numValue > maxDiscountAmount) {
          // Block the input if it exceeds 100% of applicable amount
          return;
        }
      }
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const receivedQty = Math.max(0, parseFloat(detail.Quantity) || 0);
      // In create/edit mode, stock quantity equals received quantity (damage/excess/less are set during verification)
      const stockQty = receivedQty;
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0);
      const useIGST = isIGSTInvoice();

      let taxableAmount, gstAmount, amount;

      if (isIncludingTax()) {
        // Rate includes GST - extract GST from rate (back-calculate)
        const totalWithGst = receivedQty * rate;
        taxableAmount = totalWithGst / (1 + gstRate / 100);
        amount = totalWithGst; // Amount = qty × rate (GST inclusive price)

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          // Ensure discount amount doesn't exceed 100% of base amount
          discountAmount = Math.min(discountAmount, taxableAmount);
          discountPct = taxableAmount > 0 ? (discountAmount / taxableAmount) * 100 : 0;
          // Ensure discount percentage doesn't exceed 100%
          discountPct = Math.min(discountPct, 100);
        } else {
          discountPct = Math.max(0, parseFloat(detail.DiscountPercentage) || 0);
          // Ensure discount percentage doesn't exceed 100%
          discountPct = Math.min(discountPct, 100);
          discountAmount = (taxableAmount * discountPct) / 100;
        }

        taxableAmount = taxableAmount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          StockQuantity: stockQty,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          Amount: amount,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount
        };
      } else {
        // Rate excludes GST - normal calculation
        amount = receivedQty * rate;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          // Ensure discount amount doesn't exceed 100% of amount
          discountAmount = Math.min(discountAmount, amount);
          discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
          // Ensure discount percentage doesn't exceed 100%
          discountPct = Math.min(discountPct, 100);
        } else {
          discountPct = Math.max(0, parseFloat(detail.DiscountPercentage) || 0);
          // Ensure discount percentage doesn't exceed 100%
          discountPct = Math.min(discountPct, 100);
          discountAmount = (amount * discountPct) / 100;
        }

        taxableAmount = amount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          StockQuantity: stockQty,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          Amount: amount,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount
        };
      }
    }
    setSaleDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const handleItemSelect = async (index, itemId) => {
    const selectedItemForCheck = items.find(i => i.Id === parseInt(itemId));
    const itemHasWarranty = selectedItemForCheck?.HasWarranty || false;
    const itemIsBundle = selectedItemForCheck?.IsBundle || false;
    // Check if item is already selected in another row (only in non-batch/non-warranty mode)
    const isDuplicate = !isBatchWise && !itemHasWarranty && saleDetails.some((detail, i) =>
      i !== index && detail.ItemId === parseInt(itemId)
    );

    if (isDuplicate) {
      showError('This item is already added to the table. Please select a different item.');
      return;
    }

    const selectedItem = items.find(i => i.Id === parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = [...saleDetails];
      // Ensure row has a stable _rowKey
      if (!updatedDetails[index]._rowKey) {
        updatedDetails[index] = { ...updatedDetails[index], _rowKey: `row_${Date.now()}_${Math.random()}` };
      }
      const rowKey = updatedDetails[index]._rowKey;

      // Fetch single item via API to get accurate IsInstallation flag
      let needsInstallation = !!(selectedItem.IsInstallation); // fallback from list
      try {
        console.log('[IsInstallation] Fetching item:', itemId, 'URL:', `/Item/${parseInt(itemId)}`);
        const res = await fetchData('ItemApi', `/Item/${parseInt(itemId)}`, token);
        console.log('[IsInstallation] API response:', res);
        const data = res?.response_data || res;
        const itemDetail = data?.Item || data;
        console.log('[IsInstallation] itemDetail:', itemDetail, 'IsInstallation:', itemDetail?.IsInstallation);
        if (itemDetail && typeof itemDetail.IsInstallation !== 'undefined') {
          needsInstallation = !!(itemDetail.IsInstallation);
        }
      } catch(err) {
        console.error('[IsInstallation] API error:', err);
        // keep fallback value from items list
      }
      console.log('[IsInstallation] Final needsInstallation:', needsInstallation);

      // Fetch batches for this item if batch-wise mode or item has warranty
      if (isBatchWise || selectedItem.HasWarranty) {
        fetchItemBatches(itemId, rowKey);
      }

      const receivedQty = parseFloat(updatedDetails[index].Quantity) || 0;
      const rate = selectedItem.MaxRate || selectedItem.SaleRate || 0;
      const gstRate = selectedItem.GSTRate || 0;
      const discountPct = parseFloat(updatedDetails[index].DiscountPercentage) || 0;
      const useIGST = isIGSTInvoice();

      let taxableAmount, gstAmount, amount;

      if (isIncludingTax()) {
        // Rate includes GST - extract GST from rate (back-calculate)
        const totalWithGst = receivedQty * rate;
        const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
        const discountAmount = (baseAmountBeforeGST * discountPct) / 100;
        taxableAmount = baseAmountBeforeGST - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;
        amount = totalWithGst; // Amount = qty × rate (GST inclusive price)

        updatedDetails[index] = {
          ...updatedDetails[index],
          ItemId: itemId,
          ItemName: selectedItem.Name || '',
          HSNCode: selectedItem.HSNCode || '',
          GSTRate: gstRate,
          Rate: rate,
          MinRate: parseFloat(selectedItem.MinRate) || 0,
          MaxRate: parseFloat(selectedItem.MaxRate) || 0,
          HasWarranty: selectedItem.HasWarranty || false,
          IsInstallation: needsInstallation,
          BalanceStock: parseFloat(selectedItem.CurrentBranchStock ?? selectedItem.BalanceStock) || 0,
          AssignedBranchId: parseInt(branchId) || null,
          BatchNumber: '',
          BatchId: 0,
          Amount: amount,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: receivedQty,
          DiscountPercentage: discountPct,
          DiscountAmount: discountAmount,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount
        };
      } else {
        // Rate excludes GST
        amount = receivedQty * rate;
        const discountAmount = (amount * discountPct) / 100;
        taxableAmount = amount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          ItemId: itemId,
          ItemName: selectedItem.Name || '',
          HSNCode: selectedItem.HSNCode || '',
          GSTRate: gstRate,
          Rate: rate,
          MinRate: parseFloat(selectedItem.MinRate) || 0,
          MaxRate: parseFloat(selectedItem.MaxRate) || 0,
          HasWarranty: selectedItem.HasWarranty || false,
          IsInstallation: needsInstallation,
          BalanceStock: parseFloat(selectedItem.CurrentBranchStock ?? selectedItem.BalanceStock) || 0,
          AssignedBranchId: parseInt(branchId) || null,
          BatchNumber: '',
          BatchId: 0,
          Amount: amount,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: receivedQty,
          DiscountPercentage: discountPct,
          DiscountAmount: discountAmount,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount
        };
      }
      setSaleDetails(updatedDetails);
      calculateTotals(updatedDetails);

      // Auto-tick Installation checkbox if any item in details needs installation
      const anyInstallation = updatedDetails.some(d => !!(d.IsInstallation));
      const tomorrowForInline = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })();
      setFormData(prev => ({
        ...prev,
        IsInstallation: anyInstallation,
        // Auto-set InstallationDate to tomorrow when an installation item is added (only if not already set)
        InstallationDate: anyInstallation && !prev.InstallationDate ? tomorrowForInline : prev.InstallationDate
      }));

      // If the selected item is a Bundle, fetch its sub-items
      if (itemIsBundle) {
        fetchBundleItems(itemId, index);
      } else {
        // Remove any previously loaded bundle items for this row (item changed to non-bundle)
        setSaleBundleDetails(prev => (prev || []).filter(b => b._parentRowIndex !== index));
        setBundleExpandedMap(prev => { const next = { ...prev }; delete next[index]; return next; });
      }
    }
  };

  // Handle batch selection in batch-wise mode
  const handleBatchSelect = (index, batchName) => {
    if (!isBatchWise && !saleDetails[index]?.HasWarranty) return;
    const rowKey = saleDetails[index]?._rowKey;
    const batches = batchData[rowKey]?.batches || [];
    const selectedBatch = batches.find(b => b.Name === batchName);
    const updatedDetails = [...saleDetails];

    // Set MaxRate as default rate, store MinRate/MaxRate for validation
    const maxRate = parseFloat(selectedBatch?.MaxRate) || 0;
    const minRate = parseFloat(selectedBatch?.MinRate) || 0;
    // Support multiple possible ID field names from API response
    const batchId = selectedBatch?.Id || selectedBatch?.ItemBatchId || selectedBatch?.BatchId || selectedBatch?.id || 0;
    updatedDetails[index] = {
      ...updatedDetails[index],
      BatchNumber: batchName,
      BatchId: batchId,
      Rate: maxRate,
      MinRate: minRate,
      MaxRate: maxRate,
    };

    // If quantity already entered, re-validate against batch stock (sum all rows with same batch)
    const currentQty = parseFloat(updatedDetails[index].Quantity) || 0;
    if (currentQty > 0 && selectedBatch) {
      const batchStock = parseFloat(selectedBatch.BalanceStock ?? selectedBatch.BalanceQuantity) || 0;
      const otherBatchQty = saleDetails.reduce((sum, d, i) => {
        if (i !== index && d.BatchNumber === batchName) return sum + (parseFloat(d.Quantity) || 0);
        return sum;
      }, 0);
      if (batchStock > 0 && (currentQty + otherBatchQty) > batchStock) {
        const itemData = items.find(i => i.Id === parseInt(updatedDetails[index].ItemId));
        showError(`Total quantity (${currentQty + otherBatchQty}) exceeds batch stock (${batchStock}) for ${itemData?.Name || 'this item'} [Batch: ${batchName}]`);
        updatedDetails[index] = { ...updatedDetails[index], Quantity: Math.max(0, batchStock - otherBatchQty) };
      }
    }

    calculateTotals(updatedDetails);
    setSaleDetails(updatedDetails);
    setHasUnsavedChanges(true);
  };

  const addDetailRow = () => {
    setHasUnsavedChanges(true);
    setSaleDetails([{
      Id: 0, ItemId: '', ItemName: '', BatchNumber: '', SaleOrderId: null,
      OrderQuantity: 0, Quantity: 0, BilledQuantity: 0,
      DamageQuantity: 0, ExcessQuantity: 0, LessQuantity: 0, StockQuantity: 0,
      ReturnDamageQuantity: 0, ReturnStockQuantity: 0,
      Rate: 0, Amount: 0, DiscountPercentage: 0, DiscountAmount: 0,
      HSNCode: '', GSTRate: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0, IsActive: true,
      _rowKey: `row_${Date.now()}_${Math.random()}`
    }, ...saleDetails]);
  };

  const removeDetailRow = (index) => {
    setHasUnsavedChanges(true);
    const updatedDetails = saleDetails.filter((_, i) => i !== index);
    setSaleDetails(updatedDetails);
    calculateTotals(updatedDetails);

    // Remove bundle items for this row and re-index remaining bundle rows
    setSaleBundleDetails(prev => {
      const cleaned = (prev || []).filter(b => b._parentRowIndex !== index);
      // Rows after the deleted index shift down by 1
      return cleaned.map(b => b._parentRowIndex > index ? { ...b, _parentRowIndex: b._parentRowIndex - 1 } : b);
    });
    setBundleExpandedMap(prev => {
      const next = {};
      Object.keys(prev).forEach(k => {
        const ki = parseInt(k);
        if (ki !== index) next[ki > index ? ki - 1 : ki] = prev[k];
      });
      return next;
    });
  };

  const calculateTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
    const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
    const maxItemGSTRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.GSTRate) || 0)) : 0;
    const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
    const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

    const freightAmount = parseFloat(formData.FreightAmount) || 0;
    const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
    const packingAmount = parseFloat(formData.PackingAmount) || 0;
    const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;
    const tcsAmount = parseFloat(formData.TCSAmount) || 0;
    // ExchangeAmount: only apply when IsExchange is explicitly true, capped to gross total to prevent negative grand total
    const grossTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
    const rawExchange = (formData.IsExchange === true) ? Math.max(0, parseFloat(formData.ExchangeAmount) || 0) : 0;
    const exchangeAmount = Math.min(rawExchange, grossTotal);
    const grandTotal = grossTotal - exchangeAmount;
    const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
    const totalAmount = Math.round(grandTotal);

    // Get current payment amounts
    const currentCash = parseFloat(formData.CashAmount) || 0;
    const currentUPI = parseFloat(formData.UPIAmount) || 0;
    const currentCard = parseFloat(formData.CardAmount) || 0;
    const currentTotalPayments = currentCash + currentUPI + currentCard;

    // Auto-adjust payment amounts when total changes
    let cashAmount = currentCash;
    let upiAmount = currentUPI;
    let cardAmount = currentCard;
    let creditAmount = 0;

    // If total amount is 0 (no items), clear all payment fields
    if (totalAmount === 0) {
      cashAmount = 0;
      upiAmount = 0;
      cardAmount = 0;
      creditAmount = 0;
    }
    // If Cash + UPI + Card exceeds new total, cap them proportionally
    // (only in create mode — in edit/view mode, keep existing payments as-is)
    else if (currentTotalPayments > totalAmount && modalAction === 'create') {
      // Reset payments that exceed total — treat entire amount as credit
      cashAmount = 0;
      upiAmount = 0;
      cardAmount = 0;
      creditAmount = totalAmount;
    } else {
      // CreditAmount = GrandTotal - CashAmount - CardAmount - UPIAmount
      // Cap currentTotalPayments to totalAmount to avoid negative credit
      const safePayments = Math.min(currentTotalPayments, totalAmount);
      creditAmount = Math.max(0, parseFloat((totalAmount - safePayments).toFixed(2)));
    }

    const paidAmount = cashAmount + upiAmount + cardAmount; // Credit is not counted as paid
    const balanceAmount = Math.max(0, totalAmount - paidAmount);

    if (isViewMode) {
      // In view mode, only update display totals — don't touch payment fields
      // But recalculate CreditAmount and BalanceAmount from saved payment data to avoid showing stale/corrupt values
      const viewCash = parseFloat(formData.CashAmount) || 0;
      const viewUPI = parseFloat(formData.UPIAmount) || 0;
      const viewCard = parseFloat(formData.CardAmount) || 0;
      const viewPaid = viewCash + viewUPI + viewCard;
      const viewCredit = Math.max(0, parseFloat((totalAmount - viewPaid).toFixed(2)));
      const viewBalance = Math.max(0, totalAmount - viewPaid);
      setFormData(prev => ({
        ...prev,
        ...totals,
        FreightGSTRate: effectiveFreightGSTRate,
        FreightGSTAmount: freightGSTAmount,
        PackingGSTRate: effectivePackingGSTRate,
        PackingGSTAmount: packingGSTAmount,
        RoundOff: roundOff,
        TotalAmount: totalAmount,
        GrandTotal: totalAmount,
        CreditAmount: viewCredit,
        BalanceAmount: viewBalance,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        ...totals,
        FreightGSTRate: effectiveFreightGSTRate,
        FreightGSTAmount: freightGSTAmount,
        PackingGSTRate: effectivePackingGSTRate,
        PackingGSTAmount: packingGSTAmount,
        RoundOff: roundOff,
        TotalAmount: totalAmount,
        GrandTotal: totalAmount,
        CashAmount: cashAmount,
        UPIAmount: upiAmount,
        CardAmount: cardAmount,
        CreditAmount: creditAmount,
        BankId: (upiAmount === 0 && cardAmount === 0) ? null : prev.BankId,
        BalanceAmount: balanceAmount
      }));
    }
  };

  useEffect(() => {
    // Auto-calculate totals in all modes when saleDetails loads or changes
    if (modalAction === 'create' || modalAction === 'edit' || modalAction === 'view') {
      calculateTotals(saleDetails);
    }
  }, [formData.FreightAmount, formData.PackingAmount, formData.TCSAmount, formData.IsExchange, formData.ExchangeAmount, saleDetails.length]);

  // Recalculate totals in edit mode when freight/packing amounts change (user input)
  useEffect(() => {
    if (!isViewMode && modalAction === 'edit' && saleDetails.length > 0) {
      calculateTotals(saleDetails);
    }
  }, [formData.FreightAmount, formData.PackingAmount, formData.TCSAmount]);

  useEffect(() => {
    if (!isViewMode && saleDetails.length > 0 && formData.InvoiceTypeName) {
      const useIGST = isIGSTInvoice();
      const updatedDetails = saleDetails.map(d => {
        const gstRate = parseFloat(d.GSTRate) || 0;
        const taxableAmount = parseFloat(d.TaxableAmount) || 0;
        const gstAmount = (taxableAmount * gstRate) / 100;
        return { ...d, CGSTAmount: useIGST ? 0 : gstAmount / 2, SGSTAmount: useIGST ? 0 : gstAmount / 2, IGSTAmount: useIGST ? gstAmount : 0 };
      });
      setSaleDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [formData.InvoiceTypeName]);

  // Recalculate all rows when GST calculation type changes (Excluding <-> Including)
  useEffect(() => {
    if (!isViewMode && saleDetails.length > 0) {
      const includingTax = parseInt(formData.GstCalculationType) === 2;
      const useIGST = isIGSTInvoice();
      const updatedDetails = saleDetails.map(d => {
        const receivedQty = Math.max(0, parseFloat(d.Quantity) || 0);
        const rate = Math.max(0, parseFloat(d.Rate) || 0);
        const gstRate = Math.max(0, parseFloat(d.GSTRate) || 0);
        const discountPct = Math.max(0, parseFloat(d.DiscountPercentage) || 0);

        let amount, taxableAmount, gstAmount, discountAmount;

        if (includingTax) {
          // Rate includes GST — extract GST from rate (back-calculate)
          const totalWithGst = receivedQty * rate;
          const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
          discountAmount = (baseAmountBeforeGST * discountPct) / 100;
          taxableAmount = baseAmountBeforeGST - discountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
          amount = totalWithGst; // Amount = qty × rate (GST inclusive price)
        } else {
          // Rate excludes GST — normal calculation
          amount = receivedQty * rate;
          discountAmount = (amount * discountPct) / 100;
          taxableAmount = amount - discountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
        }

        return {
          ...d,
          Amount: Math.round(amount * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100,
        };
      });
      setSaleDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.GstCalculationType]);

  // Load batches for existing items when opening in edit/view mode (batch-wise or HasWarranty)
  useEffect(() => {
    if (open && saleDetails.length > 0 && (modalAction === 'edit' || modalAction === 'view')) {
      saleDetails.forEach((detail) => {
        if (detail.ItemId && detail._rowKey && (isBatchWise || detail.HasWarranty)) {
          fetchItemBatches(detail.ItemId, detail._rowKey);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modalAction]);

  // Auto-expand bundle rows that are already loaded from API in edit/view mode
  useEffect(() => {
    if (open && (modalAction === 'edit' || modalAction === 'view') && saleBundleDetails && saleBundleDetails.length > 0) {
      const expandMap = {};
      saleBundleDetails.forEach(b => {
        if (b._parentRowIndex !== undefined && b._parentRowIndex !== null) {
          expandMap[b._parentRowIndex] = true;
        }
      });
      if (Object.keys(expandMap).length > 0) {
        setBundleExpandedMap(expandMap);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modalAction, saleBundleDetails]);

  // Set IsHold when credit days or limit exceeded, or CreditAmount > effective CreditLimit
  useEffect(() => {
    if (!ledgerGroupData || !formData.MasterId) {
      setFormData(prev => ({ ...prev, IsHold: false }));
      return;
    }
    const closingBal = parseFloat(ledgerGroupData.ClosingBalance) || 0;
    const creditLimit = parseFloat(ledgerGroupData.CreditLimit) || 0;
    const creditDays = parseInt(ledgerGroupData.CreditDays) || 0;
    const billAmount = parseFloat(formData.TotalAmount) || 0;
    const creditAmount = parseFloat(formData.CreditAmount) || 0;
    // Ledger Balance: positive = Cr (advance), negative = Dr (owes)
    const ledgerBalance = customerClosingBalance !== null ? parseFloat(customerClosingBalance) || 0 : 0;
    // Effective Credit Limit: If Cr (positive) → add to limit, If Dr (negative) → subtract from limit
    const effectiveCreditLimit = creditLimit > 0 ? creditLimit + ledgerBalance : 0;
    let maxPendingDays = parseInt(ledgerGroupData.MaxPendingDays) || 0;
    if (maxPendingDays === 0 && ledgerGroupData.OldestPendingBillDate) {
      const oldest = new Date(ledgerGroupData.OldestPendingBillDate);
      // Only use if it's a valid real date (after year 2000)
      if (oldest.getFullYear() >= 2000) {
        maxPendingDays = Math.floor((new Date() - oldest) / (1000 * 60 * 60 * 24));
      }
    }
    const daysExceeded = creditDays > 0 && maxPendingDays > 0 && maxPendingDays > creditDays;
    // Only check limit when customer has outstanding balance (pending bills exist)
    const outstanding = closingBal < 0 ? Math.abs(closingBal) : 0;
    const hasPendingBills = maxPendingDays > 0 || outstanding > 0;
    const limitExceeded = creditLimit > 0 && hasPendingBills && (outstanding + billAmount) > creditLimit;
    // Hold if CreditAmount (unpaid portion of this bill) exceeds the effective CreditLimit
    // effectiveCreditLimit = CreditLimit + LedgerBal (Cr adds, Dr subtracts)
    // If effectiveCreditLimit <= 0, customer has no credit available — any credit amount triggers hold
    const creditAmountExceedsLimit = creditLimit > 0 && (effectiveCreditLimit <= 0 ? creditAmount > 0 : creditAmount > effectiveCreditLimit);
    const isHold = daysExceeded || limitExceeded || creditAmountExceedsLimit;
    setFormData(prev => ({ ...prev, IsHold: isHold }));
  }, [ledgerGroupData, formData.TotalAmount, formData.CreditAmount, formData.MasterId, customerClosingBalance]);

  const formatCurrency = (amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getSelectedCustomerGST = () => {
    if (formData.GSTNumber) return formData.GSTNumber;
    if (!formData.MasterId) return formData.GSTNumber || '';
    const Customer = customers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return Customer?.GSTNumber || Customer?.GSTNo || '';
  };

  const getSelectedCustomerInvoiceType = () => {
    if (formData.InvoiceTypeName) return formData.InvoiceTypeName;
    if (!formData.MasterId) return formData.InvoiceTypeName || 'B to C';
    const Customer = customers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return Customer?.InvoiceTypeName || Customer?.InvoiceType || 'B to C';
  };

  const getCustomerDisplayName = () => {
    if (formData.CustomerName) return formData.CustomerName;
    if (formData.MasterName) return formData.MasterName;
    if (!formData.MasterId) return '';
    const Customer = customers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return Customer?.Name || '';
  };

  const CustomerGSTNo = getSelectedCustomerGST();
  const CustomerInvoiceType = getSelectedCustomerInvoiceType();
  const CustomerDisplayName = getCustomerDisplayName();

  const disabledFieldStyle = { '& .MuiInputBase-input.Mui-disabled': { color: '#5D4037', fontWeight: 700, WebkitTextFillColor: '#5D4037' } };
  // Handle View All Items modal opening with minimal loading
  const handleViewAllItemsOpen = () => {
    setItemsModalOpen(true);
    // No loading for this simple modal
  };

  // Customer creation functions - Remove these
  const handleViewAllItemsClose = () => {
    setItemsModalOpen(false);
  };

  // Fetch all branches for the company
  const fetchBranchList = async () => {
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      const data = response.response_data || response || [];
      setBranchList(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('Error fetching branch list:', error);
      setBranchList([]);
    }
  };

  // ── Branch Split Modal (standalone) ─────────────────────────────────────────
  const handleOpenBranchSplitModal = async (detailsOverride) => {
    setBranchSplitModalOpen(true);
    setBranchListLoading(true);

    // Use detailsOverride when provided as an array (e.g. called right after setSaleDetails to avoid
    // stale closure). Fall back to the current saleDetails prop for manual button-click calls
    // (where the first arg is a SyntheticEvent, not an array).
    const detailsToUse = Array.isArray(detailsOverride) ? detailsOverride : saleDetails;

    // Build initMap from the detail rows
    const buildInitMap = (details) => {
      const map = {};
      details.forEach(d => {
        // Guard: need a valid ItemId (> 0) and a stable _rowKey
        if (!d._rowKey || !parseInt(d.ItemId)) return;
        const qty = parseFloat(d.Quantity) || 0;

        if (d._branchSplits && d._branchSplits.length > 0) {
          // Restore previously saved branch splits (edit / view mode)
          map[d._rowKey] = d._branchSplits.map(s => ({
            splitId: `split_${Date.now()}_${Math.random()}`,
            branchId: s.branchId ? parseInt(s.branchId) : '',
            qty: parseFloat(s.qty) || 0,
            stock: s.stock ?? null,
            loading: false,
            error: ''
          }));
        } else {
          // New row — seed one entry with the row's assigned branch (if any)
          map[d._rowKey] = [
            {
              splitId: `split_${Date.now()}_${Math.random()}`,
              branchId: d.AssignedBranchId ? parseInt(d.AssignedBranchId) : '',
              qty,
              stock: null,
              loading: false,
              error: ''
            }
          ];
        }
      });
      return map;
    };

    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      const data = response.response_data || response || [];
      const fetchedBranches = Array.isArray(data) ? data : [data];

      // Set splitMap and branchList in the same state-update batch so the modal
      // renders both together — avoids the brief flash where splitMap is empty.
      setBranchList(fetchedBranches);
      setSplitMap(buildInitMap(detailsToUse));
    } catch (e) {
      console.error('Error fetching branches:', e);
      setBranchList([]);
      setSplitMap(buildInitMap(detailsToUse));
    } finally {
      setBranchListLoading(false);
    }
  };

  const handleCloseBranchSplitModal = () => {
    setBranchSplitModalOpen(false);
    setSplitMap({});
  };

  // Fetch stock for a specific split row (keyed by rowKey + splitId)
  // First tries to resolve stock from item's branch-wise fields (CHR, NPR, GD01, GD02, GD03)
  // Falls back to API call if branch code doesn't match any known field
  const fetchStockForSplitRow = async (rowKey, splitId, targetBranchId, itemId) => {
    if (!targetBranchId || !itemId) return;

    // Try to resolve stock directly from item data using branch code
    const targetBranch = branchList.find(b => b.Id === parseInt(targetBranchId));
    const branchCode = targetBranch?.Code || '';
    const itemData = items.find(i => i.Id === parseInt(itemId));

    if (itemData && branchCode) {
      // Map branch code to item's branch-wise stock field
      const branchStockMap = {
        'CHR': itemData.CHR,
        'NPR': itemData.NPR,
        'GD01': itemData.GD01,
        'GD02': itemData.GD02,
        'GD03': itemData.GD03,
      };

      const directStock = branchStockMap[branchCode.toUpperCase()];
      if (directStock !== undefined && directStock !== null) {
        const stock = parseFloat(directStock) || 0;
        setSplitMap(prev => {
          const rows = (prev[rowKey] || []).map(s =>
            s.splitId === splitId
              ? { ...s, loading: false, stock, error: stock === 0 ? 'No stock in this branch' : '' }
              : s
          );
          return { ...prev, [rowKey]: rows };
        });
        return;
      }

      // If target branch is the login branch, use CurrentBranchStock
      if (parseInt(targetBranchId) === parseInt(branchId)) {
        const stock = parseFloat(itemData.CurrentBranchStock ?? itemData.BalanceStock) || 0;
        setSplitMap(prev => {
          const rows = (prev[rowKey] || []).map(s =>
            s.splitId === splitId
              ? { ...s, loading: false, stock, error: stock === 0 ? 'No stock in this branch' : '' }
              : s
          );
          return { ...prev, [rowKey]: rows };
        });
        return;
      }
    }

    // Fallback: fetch stock from API for branches not in the pre-loaded fields
    const masterId = parseInt(formDataRef.current.MasterId) || 0;

    setSplitMap(prev => {
      const rows = (prev[rowKey] || []).map(s =>
        s.splitId === splitId ? { ...s, loading: true, stock: null, error: '' } : s
      );
      return { ...prev, [rowKey]: rows };
    });

    try {
      const res = await fetchData(
        'SaleApi',
        `/ItemStockListDetails/ItemStockListDetails/${companyId}/${branchId}/${targetBranchId}/${masterId}`,
        token
      );
      const list = Array.isArray(res.response_data || res) ? (res.response_data || res) : [];
      const found = list.find(i => i.Id === parseInt(itemId));
      const stock = found ? (parseFloat(found.CurrentBranchStock ?? found.BalanceStock) || 0) : 0;

      setSplitMap(prev => {
        const rows = (prev[rowKey] || []).map(s =>
          s.splitId === splitId
            ? { ...s, loading: false, stock, error: stock === 0 ? 'No stock in this branch' : '' }
            : s
        );
        return { ...prev, [rowKey]: rows };
      });
    } catch {
      setSplitMap(prev => {
        const rows = (prev[rowKey] || []).map(s =>
          s.splitId === splitId ? { ...s, loading: false, stock: null, error: 'Failed to fetch' } : s
        );
        return { ...prev, [rowKey]: rows };
      });
    }
  };

  // Add a split row for an item in Branch Split modal
  const handleAddSplitRow = (rowKey) => {
    setSplitMap(prev => ({
      ...prev,
      [rowKey]: [...(prev[rowKey] || []), {
        splitId: `split_${Date.now()}_${Math.random()}`,
        branchId: '',
        qty: 1,
        stock: null,
        loading: false,
        error: ''
      }]
    }));
  };

  // Remove a split row
  const handleRemoveSplitRow = (rowKey, splitId) => {
    setSplitMap(prev => ({
      ...prev,
      [rowKey]: (prev[rowKey] || []).filter(s => s.splitId !== splitId)
    }));
  };

  // Update branch on a split row
  const handleSplitRowBranchChange = (rowKey, splitId, targetBranchId, itemId) => {
    setSplitMap(prev => {
      const rows = (prev[rowKey] || []).map(s =>
        s.splitId === splitId ? { ...s, branchId: targetBranchId, stock: null, error: '' } : s
      );
      return { ...prev, [rowKey]: rows };
    });
    if (targetBranchId) fetchStockForSplitRow(rowKey, splitId, targetBranchId, itemId);
  };

  // Update qty on a split row — with real-time stock and item qty validation
  const handleSplitRowQtyChange = (rowKey, splitId, value) => {
    const num = value === '' ? '' : Math.max(0, parseFloat(value) || 0);

    // Get the item's total quantity from saleDetails
    const saleDetail = saleDetails.find(d => d._rowKey === rowKey);
    const itemTotalQty = parseFloat(saleDetail?.Quantity) || 0;

    setSplitMap(prev => {
      const updatedRows = (prev[rowKey] || []).map(s =>
        s.splitId === splitId ? { ...s, qty: num, error: '' } : s
      );

      // Validate 1: Total split qty should not exceed the item's sale qty
      const splitTotal = updatedRows.reduce((sum, s) => sum + (parseFloat(s.qty) || 0), 0);
      if (splitTotal > itemTotalQty) {
        return {
          ...prev,
          [rowKey]: updatedRows.map(s =>
            s.splitId === splitId
              ? { ...s, error: `Split total (${splitTotal}) exceeds item qty (${itemTotalQty})` }
              : s
          )
        };
      }

      // Validate 2: Total qty for same branch should not exceed stock
      const currentSplit = updatedRows.find(s => s.splitId === splitId);
      if (currentSplit && currentSplit.branchId && currentSplit.stock !== null) {
        const sameBranchTotal = updatedRows
          .filter(s => s.branchId === currentSplit.branchId)
          .reduce((sum, s) => sum + (parseFloat(s.qty) || 0), 0);
        if (sameBranchTotal > currentSplit.stock) {
          // Mark all rows of same branch with error
          return {
            ...prev,
            [rowKey]: updatedRows.map(s =>
              s.branchId === currentSplit.branchId
                ? { ...s, error: `Total qty (${sameBranchTotal}) exceeds stock (${currentSplit.stock})` }
                : s
            )
          };
        } else {
          // Clear error on same branch rows
          return {
            ...prev,
            [rowKey]: updatedRows.map(s =>
              s.branchId === currentSplit.branchId ? { ...s, error: '' } : s
            )
          };
        }
      }

      return { ...prev, [rowKey]: updatedRows };
    });
  };

  // Confirm: save branch split data on saleDetails (only affects SaleStockBranchDetails, NOT SaleDetails rows)
  const handleConfirmBranchSplit = () => {
    const errors_ = [];

    // Ensure every current saleDetails row that has a _rowKey but is missing from splitMap
    // gets a default single-branch entry — guards against stale splitMap edge cases.
    const currentSplitMap = { ...splitMap };
    saleDetails.forEach(d => {
      if (!d._rowKey || !parseInt(d.ItemId)) return;
      if (!currentSplitMap[d._rowKey]) {
        currentSplitMap[d._rowKey] = [{
          splitId: `split_${Date.now()}_${Math.random()}`,
          branchId: d.AssignedBranchId ? parseInt(d.AssignedBranchId) : '',
          qty: parseFloat(d.Quantity) || 0,
          stock: null,
          loading: false,
          error: ''
        }];
      }
    });

    for (const d of saleDetails) {
      const key = d._rowKey;
      if (!key) continue;
      const splits = currentSplitMap[key] || [];
      const noSplitConfigured = splits.length <= 1 && !splits[0]?.branchId;

      // Even with no splits configured, block apply if branch has zero stock
      // (unless it's a service item or batch/warranty item — those skip stock checks)
      const itemMeta = items.find(i => i.Id === parseInt(d.ItemId));
      const isServiceItem = itemMeta?.IsService;
      const isBatchItem = d.HasWarranty || (isBatchWise && d.ItemId);
      if (!isServiceItem && !isBatchItem && !!d.ItemId) {
        const loginBranchStock = parseFloat(d.BalanceStock ?? itemMeta?.CurrentBranchStock ?? itemMeta?.BalanceStock) || 0;
        const companyStock = parseFloat(itemMeta?.CompanyStock) || 0;
        const orderedQty = parseFloat(d.Quantity) || 0;
        if (orderedQty > 0 && loginBranchStock === 0 && companyStock === 0) {
          errors_.push(`${d.ItemName || `Item #${d.ItemId}`}: no stock available in any branch`);
          continue;
        }
        if (noSplitConfigured && orderedQty > 0 && orderedQty > loginBranchStock) {
          if (loginBranchStock === 0 && companyStock > 0) {
            errors_.push(`${d.ItemName || `Item #${d.ItemId}`}: this branch has no stock — please assign Branch Split to allocate from another branch`);
          } else {
            errors_.push(`${d.ItemName || `Item #${d.ItemId}`}: qty (${orderedQty}) exceeds this branch stock (${loginBranchStock})`);
          }
          continue;
        }
      }

      if (noSplitConfigured) continue; // no split configured, stock is fine

      const totalQty = parseFloat(d.Quantity) || 0;
      const missing = splits.find(s => !s.branchId);
      if (missing) { errors_.push(`${d.ItemName}: select a branch for all split rows`); continue; }

      const splitTotal = splits.reduce((sum, s) => sum + (parseFloat(s.qty) || 0), 0);
      if (splitTotal > totalQty) {
        errors_.push(`${d.ItemName}: split total (${splitTotal}) exceeds item qty (${totalQty})`);
        continue;
      }
      if (Math.abs(splitTotal - totalQty) > 0.001) {
        errors_.push(`${d.ItemName}: split total (${splitTotal}) ≠ item qty (${totalQty})`);
        continue;
      }

      for (const s of splits) {
        if (s.loading) { errors_.push(`${d.ItemName}: wait for stock to finish loading`); break; }
        const bName = branchList.find(b => b.Id === parseInt(s.branchId))?.Name || `Branch ${s.branchId}`;
        // Aggregate total qty for the same branch across all splits of this item
        const sameBranchTotalQty = splits
          .filter(sp => sp.branchId === s.branchId)
          .reduce((sum, sp) => sum + (parseFloat(sp.qty) || 0), 0);
        if (s.stock !== null && s.stock === 0 && sameBranchTotalQty > 0) {
          const alreadyReported = errors_.some(e => e.includes(`${d.ItemName} → ${bName}`));
          if (!alreadyReported) {
            errors_.push(`${d.ItemName} → ${bName}: no stock available (stock: 0)`);
          }
        } else if (s.stock !== null && sameBranchTotalQty > s.stock) {
          const alreadyReported = errors_.some(e => e.includes(`${d.ItemName} → ${bName}`));
          if (!alreadyReported) {
            errors_.push(`${d.ItemName} → ${bName}: total qty (${sameBranchTotalQty}) exceeds stock (${s.stock})`);
          }
        }
      }
    }

    if (errors_.length > 0) { showError(errors_.join(' | ')); return; }

    // Store branch splits on each sale detail row (does NOT modify qty/amount/row count)
    const updatedDetails = saleDetails.map(d => {
      const key = d._rowKey;
      const splits = currentSplitMap[key] || [];
      const hasRealSplits = splits.length > 0 && splits[0]?.branchId;

      if (!hasRealSplits) {
        return d;
      }

      // Store split data for SaleStockBranchDetails payload
      return {
        ...d,
        _branchSplits: splits.map(s => ({
          branchId: parseInt(s.branchId) || null,
          branchName: branchList.find(b => b.Id === parseInt(s.branchId))?.Name || `Branch #${s.branchId}`,
          qty: parseFloat(s.qty) || 0,
          stock: s.stock
        }))
      };
    });

    setSaleDetails(updatedDetails);
    setHasUnsavedChanges(true);
    handleCloseBranchSplitModal();
  };

  // Fetch item stock for a specific branch and update a split row
  // First tries to resolve stock from item's branch-wise fields (CHR, NPR, GD01, GD02, GD03)
  const fetchStockForSplit = async (itemId, splitId, targetBranchId) => {
    if (!itemId || !targetBranchId) return;

    // Try to resolve stock directly from item data using branch code
    const targetBranch = branchList.find(b => b.Id === parseInt(targetBranchId));
    const branchCode = targetBranch?.Code || '';
    const itemData = items.find(i => i.Id === parseInt(itemId));

    if (itemData && branchCode) {
      const branchStockMap = {
        'CHR': itemData.CHR,
        'NPR': itemData.NPR,
        'GD01': itemData.GD01,
        'GD02': itemData.GD02,
        'GD03': itemData.GD03,
      };

      const directStock = branchStockMap[branchCode.toUpperCase()];
      if (directStock !== undefined && directStock !== null) {
        const stock = parseFloat(directStock) || 0;
        setItemBranchSplits(prev => {
          const splits = (prev[itemId] || []).map(s =>
            s.splitId === splitId
              ? { ...s, loading: false, stock, error: stock === 0 ? 'No stock in this branch' : '' }
              : s
          );
          return { ...prev, [itemId]: splits };
        });
        return;
      }

      // If target branch is the login branch, use CurrentBranchStock
      if (parseInt(targetBranchId) === parseInt(branchId)) {
        const stock = parseFloat(itemData.CurrentBranchStock ?? itemData.BalanceStock) || 0;
        setItemBranchSplits(prev => {
          const splits = (prev[itemId] || []).map(s =>
            s.splitId === splitId
              ? { ...s, loading: false, stock, error: stock === 0 ? 'No stock in this branch' : '' }
              : s
          );
          return { ...prev, [itemId]: splits };
        });
        return;
      }
    }

    // Fallback: fetch from API
    const masterId = parseInt(formDataRef.current.MasterId) || 0;

    setItemBranchSplits(prev => {
      const splits = (prev[itemId] || []).map(s =>
        s.splitId === splitId ? { ...s, loading: true, stock: null, error: '' } : s
      );
      return { ...prev, [itemId]: splits };
    });

    try {
      const response = await fetchData(
        'SaleApi',
        `/ItemStockListDetails/ItemStockListDetails/${companyId}/${branchId}/${targetBranchId}/${masterId}`,
        token
      );
      const data = response.response_data || response || [];
      const list = Array.isArray(data) ? data : [];
      const found = list.find(i => i.Id === parseInt(itemId));
      const stock = found ? (parseFloat(found.CurrentBranchStock ?? found.BalanceStock) || 0) : 0;

      setItemBranchSplits(prev => {
        const splits = (prev[itemId] || []).map(s =>
          s.splitId === splitId
            ? { ...s, loading: false, stock, error: stock === 0 ? 'No stock in this branch' : '' }
            : s
        );
        return { ...prev, [itemId]: splits };
      });
    } catch (error) {
      console.error('Error fetching branch stock:', error);
      setItemBranchSplits(prev => {
        const splits = (prev[itemId] || []).map(s =>
          s.splitId === splitId
            ? { ...s, loading: false, stock: null, error: 'Failed to fetch stock' }
            : s
        );
        return { ...prev, [itemId]: splits };
      });
    }
  };

  // Add a new branch split row for an item
  const handleAddSplit = (itemId) => {
    const newSplit = {
      splitId: `split_${Date.now()}_${Math.random()}`,
      branchId: '',
      qty: 1,
      stock: null,
      loading: false,
      error: ''
    };
    setItemBranchSplits(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), newSplit]
    }));
  };

  // Remove a split row
  const handleRemoveSplit = (itemId, splitId) => {
    setItemBranchSplits(prev => {
      const splits = (prev[itemId] || []).filter(s => s.splitId !== splitId);
      return { ...prev, [itemId]: splits };
    });
  };

  // Update branch on a split row
  const handleSplitBranchChange = (itemId, splitId, targetBranchId) => {
    setItemBranchSplits(prev => {
      const splits = (prev[itemId] || []).map(s =>
        s.splitId === splitId ? { ...s, branchId: targetBranchId, stock: null, error: '' } : s
      );
      return { ...prev, [itemId]: splits };
    });
    if (targetBranchId) {
      fetchStockForSplit(itemId, splitId, targetBranchId);
    }
  };

  // Update qty on a split row — with real-time stock validation
  const handleSplitQtyChange = (itemId, splitId, value) => {
    const num = parseFloat(value) || 0;
    if (num < 0) return;
    setItemBranchSplits(prev => {
      const updatedSplits = (prev[itemId] || []).map(s =>
        s.splitId === splitId ? { ...s, qty: value === '' ? '' : num, error: '' } : s
      );

      // Validate: total qty for same branch should not exceed stock
      const currentSplit = updatedSplits.find(s => s.splitId === splitId);
      if (currentSplit && currentSplit.branchId && currentSplit.stock !== null) {
        const sameBranchTotal = updatedSplits
          .filter(s => s.branchId === currentSplit.branchId)
          .reduce((sum, s) => sum + (parseFloat(s.qty) || 0), 0);
        if (sameBranchTotal > currentSplit.stock) {
          return {
            ...prev,
            [itemId]: updatedSplits.map(s =>
              s.branchId === currentSplit.branchId
                ? { ...s, error: `Total qty (${sameBranchTotal}) exceeds stock (${currentSplit.stock})` }
                : s
            )
          };
        } else {
          return {
            ...prev,
            [itemId]: updatedSplits.map(s =>
              s.branchId === currentSplit.branchId ? { ...s, error: '' } : s
            )
          };
        }
      }

      return { ...prev, [itemId]: updatedSplits };
    });
  };

  // Handle branch assignment toggle and reset
  const handleItemBranchAssign = (itemId, targetBranchId) => {
    // legacy single-branch helper — kept for backward compat (unused in new UI)
    void targetBranchId;
    void itemId;
  };

  // Handle Get Items modal
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);
    setBranchAssignMode(false);
    setItemBranchSplits({});

    const loadingTimeout = setTimeout(() => {
      console.warn('Get Items loading timeout - stopping loading');
      setGetItemsLoading(false);
    }, 3000);

    try {
      // Items are already loaded in the parent component
      console.log('Items already available:', items.length);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      clearTimeout(loadingTimeout);
      setGetItemsLoading(false);
    }

    // Fetch branch list independently so modal doesn't wait for it
    fetchBranchList();
  };

  // Handle Ledger Modal
  const handleOpenGetLedgerModal = () => {
    if (!formData.MasterId) {
      showError('Please select a customer first');
      return;
    }

    // Set default date range (current month start to today)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    setLedgerFromDate(firstDay.toISOString().split('T')[0]);
    setLedgerToDate(today.toISOString().split('T')[0]);
    setLedgerModalOpen(true);

    // Fetch initial ledger data
    fetchLedgerData(formData.MasterId, firstDay.toISOString().split('T')[0], today.toISOString().split('T')[0]);
    fetchLedgerGroupData(formData.MasterId, firstDay.toISOString().split('T')[0], today.toISOString().split('T')[0]);

  };

  const handleCloseLedgerModal = () => {
    setLedgerModalOpen(false);
    setLedgerData([]);
    setLedgerFromDate('');
    setLedgerToDate('');
  };

  // Fetch ledger data
  const fetchLedgerData = async (masterId, fromDate, toDate) => {
    if (!masterId || !fromDate || !toDate) return;

    setLedgerLoading(true);
    try {
      const response = await fetchData('SaleApi', `/LedgerDetails/${companyId}/${masterId}/${fromDate}/${toDate}`, token);
      setLedgerData(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      setLedgerData([]);
      showError('Failed to fetch ledger data');
    } finally {
      setLedgerLoading(false);
    }
  };

  const fetchLedgerGroupData = async (masterId, fromDate, toDate) => {
    if (!masterId || !fromDate || !toDate) return;

    try {
      const response = await fetchData('SaleApi', `/GroupLedgerDetails/${companyId}/${masterId}/${fromDate}/${toDate}`, token);
      const data = response.response_data || response || null;
      // Handle array or object response
      const groupData = Array.isArray(data) ? data[0] : data;
      console.log('fetchLedgerGroupData raw response:', response);
      console.log('fetchLedgerGroupData groupData:', groupData);
      setLedgerGroupData(groupData || null);
    } catch (error) {
      console.error('Error fetching ledger group data:', error);
      setLedgerGroupData(null);
    }
  };

  // Handle ledger date change
  const handleLedgerDateChange = () => {
    if (ledgerFromDate && ledgerToDate && formData.MasterId) {
      fetchLedgerData(formData.MasterId, ledgerFromDate, ledgerToDate);
      fetchLedgerGroupData(formData.MasterId, ledgerFromDate, ledgerToDate);
    }
  };

  // Auto-refresh ledger when customer changes (if modal is open)
  useEffect(() => {
    if (ledgerModalOpen && formData.MasterId && ledgerFromDate && ledgerToDate) {
      fetchLedgerData(formData.MasterId, ledgerFromDate, ledgerToDate);
    } else if (ledgerModalOpen && !formData.MasterId) {
      // Close ledger modal if customer is cleared
      handleCloseLedgerModal();
    }
  }, [formData.MasterId]);

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setBranchAssignMode(false);
    setItemBranchSplits({});
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.MaxRate || item.SaleRate || 0,
        selectedDiscountPercentage: 0,
        selectedDiscountAmount: 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.Id !== item.Id));
    }
  };

  // Update selected item details
  const handleSelectedItemChange = (itemId, field, value) => {
    setSelectedItems(prev => prev.map(item => {
      if (item.Id !== itemId) return item;

      const updatedItem = { ...item, [field]: value };

      // Rate validation against MinRate/MaxRate
      if (field === 'selectedRate') {
        const numRate = parseFloat(value) || 0;
        const minRate = parseFloat(item.MinRate) || 0;
        const maxRate = parseFloat(item.MaxRate) || 0;
        if (maxRate > 0 && numRate > maxRate) {
          showError(`Rate (${numRate}) cannot exceed Max Rate (${maxRate})`);
          return { ...item, selectedRate: maxRate };
        }
        if (minRate > 0 && numRate < minRate) {
          showError(`Rate (${numRate}) cannot be less than Min Rate (${minRate})`);
          return { ...item, selectedRate: minRate };
        }
      }

      // Stock validation for quantity
      if (field === 'selectedQuantity') {
        const qty = parseFloat(value) || 0;
        const stock = parseFloat(item.CurrentBranchStock ?? item.BalanceStock) || 0;
        return {
          ...updatedItem,
          stockError: stock > 0 && qty > stock ? `Max stock: ${stock}` : ''
        };
      }

      // Handle bidirectional discount sync for selected items
      if (field === 'selectedDiscountPercentage' || field === 'selectedDiscountAmount') {
        const quantity = parseFloat(updatedItem.selectedQuantity) || 0;
        const rate = parseFloat(updatedItem.selectedRate) || 0;
        const gstRate = parseFloat(updatedItem.GSTRate) || 0;
        // For including tax, discount applies on base amount (before GST)
        const baseAmount = isIncludingTax()
          ? (quantity * rate) / (1 + gstRate / 100)
          : quantity * rate;

        if (field === 'selectedDiscountPercentage') {
          const discountPct = Math.max(0, Math.min(100, parseFloat(value) || 0));
          const discountAmount = (baseAmount * discountPct) / 100;
          return {
            ...updatedItem,
            selectedDiscountPercentage: discountPct,
            selectedDiscountAmount: discountAmount
          };
        } else if (field === 'selectedDiscountAmount') {
          const discountAmount = Math.max(0, Math.min(baseAmount, parseFloat(value) || 0));
          const discountPct = baseAmount > 0 ? (discountAmount / baseAmount) * 100 : 0;
          return {
            ...updatedItem,
            selectedDiscountAmount: discountAmount,
            selectedDiscountPercentage: Math.round(discountPct * 100) / 100
          };
        }
      }

      return updatedItem;
    }));
  };

  // Handle search in Get Items modal
  const handleGetItemsSearch = (searchValue) => {
    setGetItemsSearchValue(searchValue);
    setCurrentItemsPage(1);
  };

  // Filter items based on search value for Get Items modal
  const getFilteredItems = () => {
    if (!getItemsSearchValue.trim()) {
      return items;
    }

    const searchTerm = getItemsSearchValue.toLowerCase().trim();
    return items.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm) ||
      item.HSNCode?.toLowerCase().includes(searchTerm) ||
      item.Code?.toLowerCase().includes(searchTerm)
    );
  };

  // Get paginated items for Get Items modal
  const getPaginatedItems = () => {
    const filtered = getFilteredItems();
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = Math.ceil(getFilteredItems().length / itemsPerPage);

  // Check if item is already in sale details (not used in batch-wise or HasWarranty mode)
  const isItemAlreadyInSale = (itemId) => {
    if (isBatchWise) return false;
    const itemData = items.find(i => i.Id === parseInt(itemId));
    if (itemData?.HasWarranty) return false; // warranty items can be added multiple times (batch-per-row)
    return saleDetails.some(detail => detail.ItemId === parseInt(itemId));
  };

  // Add selected items to sale details
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    // ── Branch-assign mode validation ────────────────────────────────────────
    if (branchAssignMode) {
      const splitErrors = [];

      for (const item of selectedItems) {
        if (item.HasWarranty) continue; // batch-level checked later
        const splits = itemBranchSplits[item.Id] || [];
        const totalQty = parseFloat(item.selectedQuantity) || 0;

        if (splits.length === 0) continue; // no splits configured, skip branch check

        // Every split must have a branch selected
        const missingSplit = splits.find(s => !s.branchId);
        if (missingSplit) {
          splitErrors.push(`${item.Name}: select a branch for all split rows`);
          continue;
        }

        // Sum of split qtys must equal item qty
        const splitTotal = splits.reduce((sum, s) => sum + (parseFloat(s.qty) || 0), 0);
        if (Math.abs(splitTotal - totalQty) > 0.001) {
          splitErrors.push(`${item.Name}: split qty total (${splitTotal}) must equal item qty (${totalQty})`);
          continue;
        }

        // Each split qty must not exceed its branch stock (aggregate per branch)
        for (const s of splits) {
          if (s.loading) {
            splitErrors.push(`${item.Name}: wait for stock to finish loading`);
            break;
          }
          const branchName = branchList.find(b => b.Id === parseInt(s.branchId))?.Name || `Branch ${s.branchId}`;
          // Sum total qty for the same branch
          const sameBranchTotalQty = splits
            .filter(sp => sp.branchId === s.branchId)
            .reduce((sum, sp) => sum + (parseFloat(sp.qty) || 0), 0);
          if (s.stock !== null && sameBranchTotalQty > s.stock) {
            const alreadyReported = splitErrors.some(e => e.includes(`${item.Name} → ${branchName}`));
            if (!alreadyReported) {
              splitErrors.push(`${item.Name} → ${branchName}: total qty (${sameBranchTotalQty}) exceeds stock (${s.stock})`);
            }
          }
        }
      }

      if (splitErrors.length > 0) {
        showError(splitErrors.join(' | '));
        return;
      }
    } else {
      // Normal stock validation (no branch assignment)
      // If login branch has no stock but other branches do (CompanyStock > 0),
      // allow the item — branch split will handle the allocation.
      const stockErrors = selectedItems.filter(item => {
        if (item.HasWarranty) return false;
        if (item.IsService) return false; // service items have no physical stock
        const qty = parseFloat(item.selectedQuantity) || 0;
        const stock = parseFloat(item.CurrentBranchStock ?? item.BalanceStock) || 0;
        if (qty <= stock) return false; // stock OK for login branch
        // Login branch has insufficient stock — check if other branches have stock
        const companyStock = parseFloat(item.CompanyStock) || 0;
        if (companyStock > 0) return false; // other branches have stock, allow through
        return true; // no stock anywhere
      });
      if (stockErrors.length > 0) {
        showError(stockErrors.map(i => `${i.Name}: qty (${i.selectedQuantity}) exceeds stock (${i.CurrentBranchStock ?? i.BalanceStock})`).join(' | '));
        return;
      }
    }

    setAddingItemsLoading(true);

    try {
      const useIGST = isIGSTInvoice();

      // Helper: build a sale-detail row from an item + a specific qty + optional branchId
      const buildRow = (selectedItem, quantity, assignedBranchId = null, balStock = null, itemIsInstallation = false) => {
        const rate = parseFloat(selectedItem.selectedRate) || 0;
        const gstRate = parseFloat(selectedItem.GSTRate) || 0;
        const discountPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
        const discountAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;

        let amount, taxableAmount, gstAmount, finalDiscountAmount;

        if (isIncludingTax()) {
          const totalWithGst = quantity * rate;
          const baseBeforeGST = totalWithGst / (1 + gstRate / 100);
          // pro-rate discount proportionally to quantity split
          const fullQty = parseFloat(selectedItem.selectedQuantity) || quantity;
          const ratio = fullQty > 0 ? quantity / fullQty : 1;
          finalDiscountAmount = discountAmt > 0 ? discountAmt * ratio : (baseBeforeGST * discountPct) / 100;
          taxableAmount = baseBeforeGST - finalDiscountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
          amount = totalWithGst;
        } else {
          amount = quantity * rate;
          const fullQty = parseFloat(selectedItem.selectedQuantity) || quantity;
          const ratio = fullQty > 0 ? quantity / fullQty : 1;
          finalDiscountAmount = discountAmt > 0 ? discountAmt * ratio : (amount * discountPct) / 100;
          taxableAmount = amount - finalDiscountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
        }

        return {
          Id: 0,
          ItemId: selectedItem.Id,
          ItemName: selectedItem.Name || '',
          HSNCode: selectedItem.HSNCode || '',
          GSTRate: gstRate,
          Quantity: quantity,
          Rate: rate,
          MinRate: parseFloat(selectedItem.MinRate) || 0,
          MaxRate: parseFloat(selectedItem.MaxRate) || 0,
          HasWarranty: selectedItem.HasWarranty || false,
          IsInstallation: itemIsInstallation,
          BalanceStock: balStock !== null ? balStock : (parseFloat(selectedItem.CurrentBranchStock ?? selectedItem.BalanceStock) || 0),
          AssignedBranchId: assignedBranchId || null,
          BatchNumber: '',
          BatchId: 0,
          Amount: amount,
          DiscountPercentage: discountAmt > 0 ? (finalDiscountAmount / (amount || 1)) * 100 : discountPct,
          DiscountAmount: finalDiscountAmount,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount,
          BilledQuantity: quantity,
          OrderQuantity: quantity,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: quantity,
          IsActive: true,
          _rowKey: `row_${Date.now()}_${Math.random()}`
        };
      };

      // Build rows — items already in sale are skipped unless HasWarranty
      const newItems = [];
      for (const selectedItem of selectedItems) {
        if (isItemAlreadyInSale(selectedItem.Id)) continue;

        // Fetch IsInstallation from API for each item
        let itemIsInstallation = !!(selectedItem.IsInstallation);
        try {
          const res = await fetchData('ItemApi', `/Item/${parseInt(selectedItem.Id)}`, token);
          const data = res?.response_data || res;
          const itemDetail = data?.Item || data;
          if (itemDetail && typeof itemDetail.IsInstallation !== 'undefined') {
            itemIsInstallation = !!(itemDetail.IsInstallation);
          }
        } catch {
          // keep fallback
        }

        const splits = branchAssignMode ? (itemBranchSplits[selectedItem.Id] || []) : [];

        if (branchAssignMode && splits.length > 0) {
          // One row per split
          for (const s of splits) {
            const qty = parseFloat(s.qty) || 0;
            if (qty <= 0) continue;
            newItems.push(buildRow(selectedItem, qty, parseInt(s.branchId) || null, s.stock, itemIsInstallation));
          }
        } else {
          // Normal single row — default to login branch
          const qty = parseFloat(selectedItem.selectedQuantity) || 0;
          newItems.push(buildRow(selectedItem, qty, parseInt(branchId) || null, null, itemIsInstallation));
        }
      }

      if (newItems.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const updatedDetails = [...saleDetails, ...newItems];
      setSaleDetails(updatedDetails);
      handleCloseGetItemsModal();

      // Auto-tick Installation checkbox if any item needs installation
      const anyInstallation = updatedDetails.some(d => !!(d.IsInstallation));
      const tomorrowForPicker = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })();
      setFormData(prev => ({
        ...prev,
        IsInstallation: anyInstallation,
        // Auto-set InstallationDate to tomorrow when an installation item is added (only if not already set)
        InstallationDate: anyInstallation && !prev.InstallationDate ? tomorrowForPicker : prev.InstallationDate
      }));

      setTimeout(() => { calculateTotals(updatedDetails); }, 10);

      // Auto-open branch split modal if any newly added item has 0 login-branch stock
      // but has stock in other branches (CompanyStock > 0) — user needs to assign from other branch
      const needsBranchSplit = selectedItems.some(item => {
        if (item.HasWarranty) return false;
        const loginStock = parseFloat(item.CurrentBranchStock ?? item.BalanceStock) || 0;
        const companyStock = parseFloat(item.CompanyStock) || 0;
        return loginStock === 0 && companyStock > 0;
      });
      if (needsBranchSplit) {
        setTimeout(() => { handleOpenBranchSplitModal(updatedDetails); }, 150);
      }

      // Fetch batches for newly added items
      if (isBatchWise) {
        newItems.forEach(item => fetchItemBatches(item.ItemId, item._rowKey));
      } else {
        newItems.filter(item => item.HasWarranty).forEach(item => fetchItemBatches(item.ItemId, item._rowKey));
      }

      // Fetch bundle items
      newItems.forEach((item, newIdx) => {
        const originalItem = selectedItems.find(si => si.Id === item.ItemId);
        if (originalItem?.IsBundle) {
          fetchBundleItems(item.ItemId, saleDetails.length + newIdx);
        }
      });

    } catch (error) {
      console.error('Error adding selected items:', error);
      showError('Failed to add selected items. Please try again.');
    } finally {
      setAddingItemsLoading(false);
    }
  };

  // Filter invoice types for new customers
  const getFilteredInvoiceTypes = () => {
    if (formData.MasterId) {
      // Existing customer - show all invoice types
      return invoiceTypes;
    } else {
      // New customer - filter based on conditions
      const filteredTypes = invoiceTypes.filter(it => {
        const typeName = it.Name.toLowerCase();

        // Always show B to C and IGST related types
        if (typeName.includes('b to c') || typeName.includes('b2c') || typeName.includes('igst')) {
          return true;
        }

        // Show B to B only if GST number is entered
        if ((typeName.includes('b to b') || typeName.includes('b2b')) && formData.GSTNumber && formData.GSTNumber.trim() !== '') {
          return true;
        }

        return false;
      });

      return filteredTypes;
    }
  };

  const compactInput = { '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' }, ...(isViewMode && disabledFieldStyle) };
  return (
    <>
      <Dialog open={open} onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          if (hasUnsavedChanges && !isViewMode) {
            setCancelConfirmOpen(true);
            return;
          }
        }
        onClose();
      }}
        maxWidth="xl" fullWidth fullScreen={isMobile} disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, maxWidth: '1400px', position: 'relative' } }}>

        <DialogTitle sx={{ py: 1, px: 2, borderBottom: isViewMode ? '1px solid #e2e8f0' : '1px solid #d1fae5', bgcolor: isViewMode ? '#1e293b' : '#f0fdf4' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: isViewMode ? '#f8fafc' : '#059669', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isViewMode ? '#f8fafc' : '#064e3b', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
            </Box>
            <IconButton onClick={handleCancelClick} size="small" sx={{ p: 0.5, color: isViewMode ? '#f8fafc' : 'inherit' }}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1.5 : 2, bgcolor: isViewMode ? '#f8fafc' : '#fefffe' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669' }}>Sale Information</Typography>

            {/* Hold Banner */}
            {formData.IsHold && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#dc2626', color: '#fff', px: 1.2, py: 0.4, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700 }}>
                ⚠ This sale is on HOLD — {(() => {
                  if (ledgerGroupData && parseFloat(ledgerGroupData.CreditLimit) > 0) {
                    const cl = parseFloat(ledgerGroupData.CreditLimit);
                    const lb = customerClosingBalance !== null ? parseFloat(customerClosingBalance) || 0 : 0;
                    const effLim = Math.max(0, cl + lb);
                    if (effLim > 0 && (parseFloat(formData.CreditAmount) || 0) > effLim) return 'Credit Amount exceeds Effective Credit Limit';
                  }
                  return 'Credit limit/days exceeded';
                })()}
              </Box>
            )}

            {/* Customer Closing Balance in header */}
            {formData.MasterId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>
                  Ledger Bal:
                </Typography>
                {loadingBalance ? (
                  <CircularProgress size={12} />
                ) : (
                  <Typography variant="caption" sx={{
                    fontSize: '0.7rem', fontWeight: 700,
                    color: customerClosingBalance >= 0 ? '#059669' : '#dc2626',
                    bgcolor: customerClosingBalance >= 0 ? '#f0fdf4' : '#fef2f2',
                    px: 1, py: 0.4, borderRadius: 1,
                    border: `1px solid ${customerClosingBalance >= 0 ? '#bbf7d0' : '#fecaca'}`,
                    minWidth: '80px', textAlign: 'center', display: 'inline-block'
                  }}>
                    ₹{Math.abs(customerClosingBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {customerClosingBalance < 0 ? ' (Dr)' : ' (Cr)'}
                  </Typography>
                )}
                {ledgerGroupData && (() => {
                  const closingBal = parseFloat(ledgerGroupData.ClosingBalance) || 0;
                  const creditLimit = parseFloat(ledgerGroupData.CreditLimit) || 0;
                  const creditDays = parseInt(ledgerGroupData.CreditDays) || 0;
                  const billAmount = parseFloat(formData.TotalAmount) || 0;
                  const creditAmount = parseFloat(formData.CreditAmount) || 0;
                  // Ledger Balance: positive = Cr (advance), negative = Dr (owes)
                  const ledgerBalance = customerClosingBalance !== null ? parseFloat(customerClosingBalance) || 0 : 0;
                  // Effective Credit Limit: Cr adds, Dr subtracts
                  const effectiveCreditLimit = creditLimit > 0 ? Math.max(0, creditLimit + ledgerBalance) : 0;
                  let maxPendingDays = parseInt(ledgerGroupData.MaxPendingDays) || 0;
                  if (maxPendingDays === 0 && ledgerGroupData.OldestPendingBillDate) {
                    const oldest = new Date(ledgerGroupData.OldestPendingBillDate);
                    if (oldest.getFullYear() >= 2000) {
                      maxPendingDays = Math.floor((new Date() - oldest) / (1000 * 60 * 60 * 24));
                    }
                  }
                  const daysExceeded = creditDays > 0 && maxPendingDays > 0 && maxPendingDays > creditDays;
                  const outstanding = closingBal < 0 ? Math.abs(closingBal) : 0;
                  const hasPendingBills = maxPendingDays > 0 || outstanding > 0;
                  const limitExceeded = creditLimit > 0 && hasPendingBills && (outstanding + billAmount) > creditLimit;
                  const creditAmountExceedsLimit = creditLimit > 0 && (effectiveCreditLimit <= 0 ? creditAmount > 0 : creditAmount > effectiveCreditLimit);
                  return (
                    <>
                      {creditAmountExceedsLimit && (
                        <Typography variant="caption" sx={{
                          fontSize: '0.65rem', fontWeight: 700, color: '#fff', bgcolor: '#dc2626',
                          px: 0.8, py: 0.3, borderRadius: 1, display: 'inline-flex', alignItems: 'center'
                        }}>
                          {effectiveCreditLimit <= 0 ? '⚠ No Credit Available (Limit exhausted by Ledger Dr balance)' : `⚠ Credit Amount (₹${creditAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}) > Eff. Limit (₹${effectiveCreditLimit.toLocaleString('en-IN', { minimumFractionDigits: 2 })})`}
                        </Typography>
                      )}
                      {limitExceeded && !creditAmountExceedsLimit && (
                        <Typography variant="caption" sx={{
                          fontSize: '0.65rem', fontWeight: 700, color: '#fff', bgcolor: '#dc2626',
                          px: 0.8, py: 0.3, borderRadius: 1, display: 'inline-flex', alignItems: 'center'
                        }}>
                          ⚠ Credit Limit Exceeded
                        </Typography>
                      )}
                      {daysExceeded && (
                        <Typography variant="caption" sx={{
                          fontSize: '0.65rem', fontWeight: 700, color: '#fff', bgcolor: '#dc2626',
                          px: 0.8, py: 0.3, borderRadius: 1, display: 'inline-flex', alignItems: 'center'
                        }}>
                          ⚠ Credit Days Exceeded
                        </Typography>
                      )}
                    </>
                  );
                })()}
                <Button
                  size="small"
                  onClick={handleOpenGetLedgerModal}
                  sx={{ minWidth: 'auto', px: 0.75, py: 0.25, fontSize: '0.65rem', bgcolor: '#1e40af', color: '#fff', '&:hover': { bgcolor: '#1d4ed8' } }}
                >
                  Ledger
                </Button>
              </Box>
            )}
          </Box>

          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={6} sm={1.2}>
              <TextField
                fullWidth
                size="small"
                label="Bill No *"
                value={formData.BillPrefix && formData.FinYear
                  ? `${formData.BillPrefix}/${formData.BillNumber}/${formData.FinYear}`
                  : formData.BillNumber || ''}
                onChange={(e) => handleChange('BillNumber', e.target.value)}
                onBlur={() => handleBlur('BillNumber')}
                disabled={isViewMode}
                error={touched.BillNumber && !!errors.BillNumber}
                helperText={touched.BillNumber && errors.BillNumber}
                sx={compactInput}
              />
            </Grid>
            <Grid item xs={6} sm={1.2}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="Bill Date"
                  value={formatDate(formData.BillDate) || '-'} disabled
                  InputLabelProps={{ shrink: true }} sx={compactInput} />
              ) : (
                <TextField
                  fullWidth size="small" type="date" label="Bill Date *"
                  value={formData.BillDate || ''}
                  onChange={(e) => handleChange('BillDate', e.target.value)}
                  onBlur={() => handleBlur('BillDate')}
                  error={touched.BillDate && !!errors.BillDate}
                  helperText={touched.BillDate && errors.BillDate}
                  InputLabelProps={{ shrink: true }} sx={compactInput}
                />
              )}
            </Grid>
            <Grid item xs={6} sm={3.7}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="Customer" value={CustomerDisplayName || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
              ) : (
                <Box>
                  {/* Customer Closing Balance - Show above customer field */}

                  <Autocomplete
                    freeSolo
                    size="small"
                    options={customers.map(customer => customer.Name)}
                    value={formData.CustomerName || CustomerDisplayName || ''}
                    onChange={(event, newValue) => {
                      handleChange('CustomerName', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setCustomerInputValue(newInputValue);
                      handleChange('CustomerName', newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Customer *"
                        error={touched.MasterId && !!errors.MasterId}
                        helperText={touched.MasterId && errors.MasterId}
                        onBlur={() => {
                          // Only validate if the field is actually empty
                          const currentValue = formData.CustomerName || '';
                          if (currentValue.trim() === '') {
                            handleBlur('MasterId');
                          } else {
                            // Clear error if there's a value
                            setErrors(prev => ({ ...prev, MasterId: '' }));
                          }
                        }}
                        sx={{
                          ...compactInput,
                          '& .MuiInputBase-root': {
                            height: '30px',
                            minHeight: '30px',
                            padding: '0 9px !important'
                          },
                          '& .MuiInputBase-input': {
                            padding: '0 !important',
                            fontSize: '0.8rem',
                            height: '30px'
                          },
                          '& .MuiAutocomplete-endAdornment': {
                            top: '50%',
                            transform: 'translateY(-50%)'
                          }
                        }}

                      />
                    )}
                    sx={{ width: '100%' }}
                    ListboxProps={{
                      style: { maxHeight: 200, fontSize: '0.85rem' }
                    }}
                  />
                </Box>
              )}
            </Grid>

            {/* Contact Number - auto-fetched from customer, editable */}
            <Grid item xs={6} sm={1.5}>
              <TextField
                fullWidth size="small" label="Contact No"
                value={formData.CustomerContactNumber || ''}
                onChange={(e) => handleChange('CustomerContactNumber', e.target.value)}
                disabled={isViewMode}
                sx={{
                  ...compactInput,
                  ...(isViewMode && disabledFieldStyle),
                  '& .MuiInputBase-root': {
                    bgcolor: formData.CustomerContactNumber ? '#eff6ff' : '#f9fafb'
                  }
                }}
              />
            </Grid>

            <Grid item xs={6} sm={1.5}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="GST No" value={CustomerGSTNo || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: CustomerGSTNo ? '#dcfce7' : '#f9fafb' } }} />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="GST No"
                  value={formData.GSTNumber || ''}
                  onChange={(e) => handleChange('GSTNumber', e.target.value)}
                  disabled={formData.MasterId ? true : false} // Editable for new customers, disabled for existing
                  sx={{
                    ...compactInput,
                    ...(formData.MasterId && disabledFieldStyle),
                    '& .MuiInputBase-root': {
                      bgcolor: formData.GSTNumber ? '#dcfce7' : '#f9fafb'
                    }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={6} sm={1}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="Inv Type" value={CustomerInvoiceType || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: CustomerInvoiceType ? '#d1fae5' : '#f9fafb' } }} />
              ) : formData.MasterId ? (
                // Existing customer - show invoice type as disabled field
                <TextField
                  fullWidth
                  size="small"
                  label="Inv Type"
                  value={CustomerInvoiceType || '-'}
                  disabled
                  sx={{
                    ...compactInput,
                    ...disabledFieldStyle,
                    '& .MuiInputBase-root': { bgcolor: CustomerInvoiceType ? '#d1fae5' : '#f9fafb' }
                  }}
                />
              ) : (
                // New customer - show invoice type as editable dropdown
                <FormControl fullWidth size="small" sx={compactInput}>
                  <InputLabel>Invoice Type</InputLabel>
                  <Select
                    value={formData.InvoiceTypeId || ''}
                    onChange={(e) => handleChange('InvoiceTypeId', e.target.value)}
                    label="Invoice Type"
                    sx={{ fontSize: '0.8rem', bgcolor: '#d1fae5' }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                  >
                    {getFilteredInvoiceTypes().map(it => (
                      <MenuItem key={it.Id} value={it.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                        {it.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            {/* State Dropdown - Show only for IGST invoice type */}
            {(formData.InvoiceTypeName && formData.InvoiceTypeName.toLowerCase().includes('igst')) && (
              <Grid item xs={6} sm={1.4}>
                {isViewMode ? (
                  <TextField fullWidth size="small" label="State" value={states.find(s => s.Id === formData.StateId)?.Name || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                ) : (
                  <FormControl fullWidth size="small" sx={compactInput}>
                    <InputLabel>State *</InputLabel>
                    <Select
                      value={formData.StateId || ''}
                      onChange={(e) => handleChange('StateId', e.target.value)}
                      label="State *"
                      sx={{ fontSize: '0.8rem' }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                    >
                      {states.map(state => (
                        <MenuItem key={state.Id} value={state.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                          {state.StateName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>
            )}
            <Grid item xs={6} sm={(!formData.MasterId && formData.InvoiceTypeName && formData.InvoiceTypeName.toLowerCase().includes('igst')) ? 1.7 : 1.7}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="Salesman" value={formData.SalesManName || salesmen.find(s => String(s.Id) === String(formData.SalesManId))?.Name || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
              ) : (
                <FormControl fullWidth size="small" sx={compactInput}>
                  <InputLabel>Salesman</InputLabel>
                  <Select
                    value={formData.SalesManId || ''}
                    onChange={(e) => handleChange('SalesManId', e.target.value)}
                    label="Salesman"
                    sx={{ fontSize: '0.8rem' }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                  >
                    {salesmen.map(salesman => (
                      <MenuItem key={salesman.Id} value={salesman.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                        {salesman.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: errors.saleDetails ? '#dc2626' : (isViewMode ? '#475569' : '#059669') }}>Sale Items *</Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>({saleDetails.length} items)</Typography>
              {errors.saleDetails && (
                <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.7rem' }}>({errors.saleDetails})</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {saleDetails.length > 5 && (
                <Button
                  size="small"
                  onClick={handleViewAllItemsOpen}
                  startIcon={<Visibility sx={{ fontSize: 14 }} />}
                  sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#1e40af' }}
                >
                  View All
                </Button>
              )}

              {!isViewMode && (
                <>
                  <Button
                    size="small"
                    startIcon={<List sx={{ fontSize: 14 }} />}
                    onClick={handleOpenGetItemsModal}
                    variant="text"
                    sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#059669' }}
                  >
                    Get Items
                  </Button>
                  {saleDetails.length > 0 && (
                    <Button
                      size="small"
                      onClick={handleOpenBranchSplitModal}
                      variant="outlined"
                      sx={{
                        py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0,
                        borderColor: '#7c3aed', color: '#7c3aed',
                        '&:hover': { bgcolor: '#f5f3ff', borderColor: '#6d28d9' }
                      }}
                    >
                      🏬 Branch Split
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* Desktop Table View - Fixed height for 5 rows */}
          {!isMobile && (
            <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 1.5, border: errors.saleDetails ? '1px solid #dc2626' : (isViewMode ? '1px solid #e2e8f0' : '1px solid #d1fae5'), borderRadius: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: isViewMode ? 'linear-gradient(135deg, #334155 0%, #1e293b 100%)' : 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #d1fae5', width: '4%', background: 'transparent', color: 'white', fontWeight: 600 }}>Sl No</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1fae5', width: '38%', background: 'transparent', color: 'white', fontWeight: 600 }}>Item</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #d1fae5', width: '6%', background: 'transparent', color: 'white', fontWeight: 600 }}>Qty</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #d1fae5', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>Rate</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #d1fae5', width: '6%', background: 'transparent', color: 'white', fontWeight: 600 }}>Disc%</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #d1fae5', width: '10%', background: 'transparent', color: 'white', fontWeight: 600 }}>Disc Amt</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #d1fae5', width: '5%', background: 'transparent', color: 'white', fontWeight: 600 }}>GST%</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #d1fae5', width: '10%', background: 'transparent', color: 'white', fontWeight: 600 }}>GST Amt</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #d1fae5', width: '9%', background: 'transparent', color: 'white', fontWeight: 600 }}>Taxable</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #d1fae5', width: '10%', background: 'transparent', color: 'white', fontWeight: 600 }}>Total</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #d1fae5', width: '6%', background: 'transparent', color: 'white', fontWeight: 600 }}>Stock</th>
                    {!isViewMode && <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #d1fae5', width: '4%', background: 'transparent', color: 'white', fontWeight: 600 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {saleDetails.map((d, index) => {
                    // Per-row stock status (skipped for service items, batch items, and branch-split rows)
                    const itemMeta = items.find(i => i.Id === parseInt(d.ItemId));
                    const isServiceItem = itemMeta?.IsService;
                    const hasSplits = d._branchSplits && d._branchSplits.length > 0;
                    const branchStock = parseFloat(d.BalanceStock ?? itemMeta?.CurrentBranchStock ?? itemMeta?.BalanceStock) || 0;
                    const orderedQty = parseFloat(d.Quantity) || 0;
                    const isOutOfStock = !isServiceItem && !hasSplits && !d.HasWarranty && !!d.ItemId && branchStock === 0;
                    const isOverQty   = !isServiceItem && !hasSplits && !d.HasWarranty && !!d.ItemId && orderedQty > 0 && orderedQty > branchStock;
                    // Row background: red tint when over-stock, purple for install, else alternating green/white
                    const rowBg = d.IsInstallation
                      ? (index % 2 === 0 ? '#faf5ff' : '#f3e8ff')
                      : isOverQty
                        ? (index % 2 === 0 ? '#fff1f2' : '#ffe4e6')
                        : (index % 2 === 0 ? (isViewMode ? '#f1f5f9' : '#f0fdf4') : '#ffffff');
                    return (
                    <React.Fragment key={index}>
                    <tr style={{ backgroundColor: rowBg }}>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'center', fontWeight: 600, color: isViewMode ? '#475569' : '#059669' }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', fontWeight: isViewMode ? 600 : 400 }}>
                        <span style={{ fontWeight: 600, color: '#064e3b', display: 'block' }}>
                          {d.ItemName || items.find(i => i.Id === parseInt(d.ItemId))?.Name || '-'}
                          {d.IsInstallation && (
                            <span style={{ marginLeft: 6, fontSize: '0.65rem', fontWeight: 700, color: '#7c3aed', background: '#ede9fe', borderRadius: 10, padding: '1px 7px', verticalAlign: 'middle' }}>🔧 Installation</span>
                          )}
                          {/* Stock warning badge inline with item name */}
                          {!isViewMode && isOverQty && (
                            <span style={{ marginLeft: 6, fontSize: '0.6rem', fontWeight: 700, color: '#dc2626', background: '#fee2e2', borderRadius: 10, padding: '1px 6px', verticalAlign: 'middle' }}>
                              {isOutOfStock ? '⊘ No Stock' : `⚠ Over by ${(orderedQty - branchStock).toFixed(2)}`}
                            </span>
                          )}
                        </span>
                        {(isBatchWise || d.HasWarranty) && (
                          isViewMode ? (
                            d.BatchNumber ? <span style={{ fontSize: '0.7rem', color: '#059669', fontWeight: 600 }}>Batch: {d.BatchNumber}</span> : null
                          ) : (
                            <Box sx={{ mt: 0.5 }}>
                              {batchData[d._rowKey]?.loading ? (
                                <CircularProgress size={12} />
                              ) : (
                                <Select
                                  size="small"
                                  value={d.BatchNumber || ''}
                                  onChange={(e) => handleBatchSelect(index, e.target.value)}
                                  displayEmpty
                                  disabled={!d.ItemId}
                                  sx={{
                                    fontSize: '0.7rem',
                                    minWidth: 260,
                                    bgcolor: d.BatchNumber ? '#f0fdf4' : '#fff',
                                    border: '1px solid',
                                    borderColor: d.BatchNumber ? '#059669' : '#d1d5db',
                                    borderRadius: '6px',
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                    '& .MuiSelect-select': { py: 0.3, px: 0.75, color: d.BatchNumber ? '#064e3b' : '#6b7280', fontWeight: d.BatchNumber ? 600 : 400 },
                                    '& .MuiSvgIcon-root': { color: '#059669', fontSize: '1rem' },
                                    '&:hover': { borderColor: '#059669' },
                                    '&.Mui-disabled': { bgcolor: '#f9fafb', borderColor: '#e5e7eb' }
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        maxHeight: 420,
                                        minWidth: 380,
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 16px rgba(5,150,105,0.15)',
                                        border: '1px solid #d1fae5',
                                        '& .MuiMenuItem-root': {
                                          fontSize: '0.75rem',
                                          py: 0.75,
                                          '&:hover': { bgcolor: '#f0fdf4', color: '#064e3b' },
                                          '&.Mui-selected': { bgcolor: '#d1fae5', color: '#064e3b', fontWeight: 600 }
                                        }
                                      }
                                    }
                                  }}
                                >
                                  <MenuItem value="" sx={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>Select Batch</MenuItem>
                                  {(batchData[d._rowKey]?.batches || []).map(b => (
                                    <MenuItem key={b.Id ?? b.ItemBatchId ?? b.BatchId ?? b.id ?? b.Name} value={b.Name}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                        <span style={{ fontWeight: 600 }}>{b.Name}</span>
                                        {b.BalanceStock != null && <span style={{ color: '#059669', fontSize: '0.7rem' }}>Qty: {b.BalanceStock ?? b.BalanceQuantity}</span>}
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            </Box>
                          )
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#064e3b' }}>{d.Quantity || 0}</span>
                        ) : (
                          <TextField size="small" type="number" value={d.Quantity ?? ''} onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} disabled={isViewMode || ((isBatchWise || d.HasWarranty) && !d.BatchNumber)} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} title={(isBatchWise || d.HasWarranty) && !d.BatchNumber ? 'Select a batch first' : ''} />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#064e3b' }}>{d.Rate || 0}</span>
                        ) : (
                          <TextField size="small" type="number" value={d.Rate ?? ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#064e3b' }}>{d.DiscountPercentage || 0}%</span>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={d.DiscountPercentage ?? 0}
                            onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)}
                            disabled={isViewMode || !d.Quantity || parseFloat(d.Quantity) <= 0}
                            onFocus={(e) => e.target.select()}
                            inputProps={{ min: 0, max: 100, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }}
                            sx={{ width: '100%' }}
                            title={`Discount percentage (max 100%)`}
                          />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#064e3b' }}>{formatCurrency(d.DiscountAmount)}</span>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={d.DiscountAmount ?? 0}
                            onChange={(e) => handleDetailChange(index, 'DiscountAmount', e.target.value)}
                            disabled={isViewMode || !d.Quantity || parseFloat(d.Quantity) <= 0}
                            onFocus={(e) => e.target.select()}
                            inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }}
                            sx={{ width: '100%' }}
                            title={`Discount amount (max ₹${getMaxDiscountAmount(d.Quantity, d.Rate, d.GSTRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of ${isIncludingTax() ? 'base' : 'item'} amount)`}
                          />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'center', fontWeight: 600, color: '#059669' }}>{d.GSTRate || 0}%</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                        {isIGSTInvoice() ? formatCurrency(d.IGSTAmount) : formatCurrency((d.CGSTAmount || 0) + (d.SGSTAmount || 0))}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'right', fontWeight: 600, color: '#064e3b' }}>{formatCurrency(d.TaxableAmount)}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(d.TotalAmount)}</td>
                      {/* Branch stock cell — colored by stock level */}
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'center' }}>
                        {d.ItemId && !d.HasWarranty && !isServiceItem ? (
                          hasSplits ? (
                            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#7c3aed', background: '#ede9fe', borderRadius: 8, padding: '1px 6px' }}>Split</span>
                          ) : (
                            <span style={{
                              fontSize: '0.65rem', fontWeight: 700, borderRadius: 8, padding: '2px 6px',
                              background: isOverQty ? '#fee2e2' : (branchStock > 0 ? '#dcfce7' : '#fee2e2'),
                              color:      isOverQty ? '#dc2626' : (branchStock > 0 ? '#166534' : '#dc2626'),
                              border: isOverQty ? '1px solid #fca5a5' : (branchStock === 0 ? '1px solid #fca5a5' : '1px solid #86efac'),
                            }}>
                              {branchStock}
                            </span>
                          )
                        ) : (
                          <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>—</span>
                        )}
                      </td>
                      {!isViewMode && (
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #d1fae5', textAlign: 'center' }}>
                          <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.25, color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                        </td>
                      )}
                    </tr>
                    {/* Bundle Sub-items Row */}
                    {(() => {
                      const bundleRows = (saleBundleDetails || []).filter(b => b._parentRowIndex === index);
                      const isLoading = bundleLoadingMap[d.ItemId];
                      const isExpanded = bundleExpandedMap[index];
                      if (!isLoading && bundleRows.length === 0) return null;
                      return (
                        <>
                          {/* Bundle section header */}
                          <tr key={`bundle-header-${index}`} style={{ backgroundColor: '#f5f3ff' }}>
                            <td colSpan={isViewMode ? 11 : 12} style={{ padding: '3px 12px', borderBottom: '1px solid #ddd6fe' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => setBundleExpandedMap(prev => ({ ...prev, [index]: !prev[index] }))}
                                  sx={{ p: 0.25, color: '#7c3aed' }}
                                >
                                  {isExpanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                                </IconButton>
                                <Chip label="Bundle" size="small" sx={{ bgcolor: '#7c3aed', color: '#fff', fontSize: '0.6rem', height: 16, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }} />
                                <Typography sx={{ fontSize: '0.65rem', color: '#6d28d9', fontWeight: 600 }}>
                                  {isLoading ? 'Loading bundle items...' : `${bundleRows.length} bundle item${bundleRows.length !== 1 ? 's' : ''}`}
                                </Typography>
                                {isLoading && <CircularProgress size={12} sx={{ color: '#7c3aed', ml: 0.5 }} />}
                              </Box>
                            </td>
                          </tr>
                          {/* Bundle item rows */}
                          {isExpanded && bundleRows.map((br, bi) => (
                            <tr key={`bundle-${index}-${bi}`} style={{ backgroundColor: '#faf5ff' }}>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'center', color: '#7c3aed', fontSize: '0.65rem' }}>↳</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', color: '#4c1d95', fontSize: '0.7rem', fontWeight: 500 }}>{br.ItemName || '-'}</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'center' }}>
                {isViewMode ? (
                  <span style={{ color: '#4c1d95', fontSize: '0.7rem', fontWeight: 600 }}>{br.Quantity}</span>
                ) : (
                  <TextField
                    size="small"
                    type="number"
                    value={br.Quantity ?? ''}
                    onChange={(e) => handleBundleQtyChange(index, bi, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    inputProps={{ min: 0, style: { textAlign: 'center', padding: '2px 4px', fontSize: '0.75rem', color: '#4c1d95', fontWeight: 600 } }}
                    sx={{ width: '100%', '& .MuiOutlinedInput-root': { bgcolor: '#f3f0ff', '& fieldset': { borderColor: '#c4b5fd' }, '&:hover fieldset': { borderColor: '#7c3aed' } } }}
                  />
                )}
              </td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'right', color: '#4c1d95', fontSize: '0.7rem' }}>{formatCurrency(br.Rate)}</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>-</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>-</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>-</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>-</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>-</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'right', color: '#4c1d95', fontSize: '0.7rem', fontWeight: 600 }}>{formatCurrency(br.Amount)}</td>
                              <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>—</td>
                              {!isViewMode && <td style={{ padding: '3px 8px', borderBottom: '1px solid #ede9fe' }}></td>}
                            </tr>
                          ))}
                        </>
                      );
                    })()}

                    </React.Fragment>
                    );
                  })}
                  {saleDetails.length === 0 && (
                    <tr><td colSpan={isViewMode ? 11 : 12} style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: isViewMode ? '#f1f5f9' : '#f0fdf4' }}>No items added</td></tr>
                  )}
                </tbody>
              </table>
            </Box>
          )}

          {/* Mobile Card View */}
          {isMobile && (
            <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 1.5 }}>
              {saleDetails.map((d, index) => {
                const mItemMeta = items.find(i => i.Id === parseInt(d.ItemId));
                const mIsService = mItemMeta?.IsService;
                const mHasSplits = d._branchSplits && d._branchSplits.length > 0;
                const mStock = parseFloat(d.BalanceStock ?? mItemMeta?.CurrentBranchStock ?? mItemMeta?.BalanceStock) || 0;
                const mQty = parseFloat(d.Quantity) || 0;
                const mOverQty = !mIsService && !mHasSplits && !d.HasWarranty && !!d.ItemId && mQty > 0 && mQty > mStock;
                return (
                <Box key={index} sx={{
                  border: '1px solid',
                  borderColor: mOverQty ? '#fca5a5' : (d.IsInstallation ? '#c4b5fd' : '#d1fae5'),
                  borderRadius: 2, p: 1.5, mb: 1.5,
                  bgcolor: mOverQty ? '#fff1f2' : (d.IsInstallation ? '#faf5ff' : '#f0fdf4')
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#064e3b', fontSize: '0.8rem' }}>
                        {d.ItemName || items.find(i => i.Id === parseInt(d.ItemId))?.Name || 'Select Item'}
                      </Typography>
                      {d.IsInstallation && (
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#7c3aed', background: '#ede9fe', borderRadius: 10, padding: '1px 7px' }}>🔧 Installation</span>
                      )}
                      {!isViewMode && mOverQty && (
                        <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#dc2626', background: '#fee2e2', borderRadius: 10, padding: '1px 6px', marginLeft: 6 }}>
                          {mStock === 0 ? '⊘ No Stock' : `⚠ Over by ${(mQty - mStock).toFixed(2)}`}
                        </span>
                      )}
                    </Box>
                    {!isViewMode && (
                      <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.5, bgcolor: '#fef2f2', color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.65rem', fontWeight: 600 }}>Batch</Typography>
                      {(isBatchWise || d.HasWarranty) ? (
                        isViewMode ? (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#064e3b', py: 0.75 }}>{d.BatchNumber || '-'}</Typography>
                        ) : (
                          batchData[d._rowKey]?.loading ? <CircularProgress size={16} /> : (
                            <Select
                              size="small"
                              value={d.BatchNumber || ''}
                              onChange={(e) => handleBatchSelect(index, e.target.value)}
                              displayEmpty
                              disabled={!d.ItemId}
                              fullWidth
                              sx={{
                                fontSize: '0.8rem',
                                bgcolor: d.BatchNumber ? '#f0fdf4' : '#fff',
                                border: '1px solid',
                                borderColor: d.BatchNumber ? '#059669' : '#d1d5db',
                                borderRadius: '6px',
                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                '& .MuiSelect-select': { py: 0.75, color: d.BatchNumber ? '#064e3b' : '#6b7280', fontWeight: d.BatchNumber ? 600 : 400 },
                                '& .MuiSvgIcon-root': { color: '#059669' },
                                '&:hover': { borderColor: '#059669' },
                                '&.Mui-disabled': { bgcolor: '#f9fafb', borderColor: '#e5e7eb' }
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: 220,
                                    minWidth: 280,
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 16px rgba(5,150,105,0.15)',
                                    border: '1px solid #d1fae5',
                                    '& .MuiMenuItem-root': {
                                      fontSize: '0.8rem',
                                      py: 0.75,
                                      '&:hover': { bgcolor: '#f0fdf4', color: '#064e3b' },
                                      '&.Mui-selected': { bgcolor: '#d1fae5', color: '#064e3b', fontWeight: 600 }
                                    }
                                  }
                                }
                              }}
                            >
                              <MenuItem value="" sx={{ fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>Select Batch</MenuItem>
                              {(batchData[d._rowKey]?.batches || []).map(b => (
                                <MenuItem key={b.Id ?? b.ItemBatchId ?? b.BatchId ?? b.id ?? b.Name} value={b.Name}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                    <span style={{ fontWeight: 600 }}>{b.Name}</span>
                                    {b.BalanceStock != null && <span style={{ color: '#059669', fontSize: '0.75rem' }}>Qty: {b.BalanceStock ?? b.BalanceQuantity}</span>}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          )
                        )
                      ) : (
                        <TextField size="small" value={d.BatchNumber || ''} onChange={(e) => handleDetailChange(index, 'BatchNumber', e.target.value)} onClick={(e) => e.stopPropagation()} disabled={isViewMode} fullWidth sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.65rem', fontWeight: 600 }}>Qty</Typography>
                      <TextField size="small" type="number" value={d.Quantity || ''} onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} onClick={(e) => e.stopPropagation()} disabled={isViewMode || ((isBatchWise || d.HasWarranty) && !d.BatchNumber)} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' } }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.65rem', fontWeight: 600 }}>Rate</Typography>
                      <TextField size="small" type="number" value={d.Rate || ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} onClick={(e) => e.stopPropagation()} disabled={isViewMode} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right' } }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #d1fae5' }}>
                    <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>Total</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(d.TotalAmount)}</Typography>
                  </Box>
                  {/* Bundle sub-items for mobile */}
                  {(() => {
                    const bundleRows = (saleBundleDetails || []).filter(b => b._parentRowIndex === index);
                    const isLoadingBundle = bundleLoadingMap[d.ItemId];
                    if (!isLoadingBundle && bundleRows.length === 0) return null;
                    const isExpanded = bundleExpandedMap[index];
                    return (
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #ede9fe' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
                          onClick={() => setBundleExpandedMap(prev => ({ ...prev, [index]: !prev[index] }))}>
                          {isExpanded ? <ExpandLess sx={{ fontSize: 14, color: '#7c3aed' }} /> : <ExpandMore sx={{ fontSize: 14, color: '#7c3aed' }} />}
                          <Chip label="Bundle" size="small" sx={{ bgcolor: '#7c3aed', color: '#fff', fontSize: '0.55rem', height: 14, fontWeight: 700, '& .MuiChip-label': { px: 0.5 } }} />
                          <Typography sx={{ fontSize: '0.65rem', color: '#6d28d9', fontWeight: 600 }}>
                            {isLoadingBundle ? 'Loading...' : `${bundleRows.length} items`}
                          </Typography>
                          {isLoadingBundle && <CircularProgress size={10} sx={{ color: '#7c3aed' }} />}
                        </Box>
                        {isExpanded && bundleRows.map((br, bi) => (
                          <Box key={bi} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, px: 0.5, bgcolor: '#faf5ff', borderRadius: 0.5, mb: 0.25, gap: 1 }}>
                            <Typography sx={{ fontSize: '0.65rem', color: '#4c1d95', fontWeight: 500, flex: 1 }}>↫ {br.ItemName || '-'}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {isViewMode ? (
                                <Typography sx={{ fontSize: '0.65rem', color: '#4c1d95', fontWeight: 600 }}>Qty: {br.Quantity}</Typography>
                              ) : (
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Qty"
                                  value={br.Quantity ?? ''}
                                  onChange={(e) => handleBundleQtyChange(index, bi, e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  inputProps={{ min: 0, style: { textAlign: 'center', padding: '2px 4px', fontSize: '0.7rem', color: '#4c1d95', fontWeight: 600, width: '48px' } }}
                                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f3f0ff', '& fieldset': { borderColor: '#c4b5fd' }, '&:hover fieldset': { borderColor: '#7c3aed' } }, '& .MuiInputLabel-root': { fontSize: '0.65rem' } }}
                                />
                              )}
                              <Typography sx={{ fontSize: '0.65rem', color: '#4c1d95', fontWeight: 600 }}>× {formatCurrency(br.Rate)}</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    );
                  })()}

                </Box>
                );
              })}
              {saleDetails.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: '#059669', bgcolor: '#f0fdf4', borderRadius: 1, border: '1px dashed #d1fae5' }}><Typography variant="body2">No items added</Typography></Box>
              )}
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Totals Section */}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={8}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669', mb: 1, display: 'block' }}>Additional Charges</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f0fdf4', p: 1.5, borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField fullWidth size="small" label="Freight" type="number" value={formData.FreightAmount || ''} onChange={(e) => handleChange('FreightAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField fullWidth size="small" label="Packing" type="number" value={formData.PackingAmount || ''} onChange={(e) => handleChange('PackingAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField fullWidth size="small" label="TCS Amount" type="number" value={formData.TCSAmount || ''} onChange={(e) => handleChange('TCSAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>

                  {/* Payment Fields Row */}
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField fullWidth size="small" label="Cash Amount" type="number" value={formData.CashAmount || ''} onChange={(e) => handleChange('CashAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField fullWidth size="small" label="UPI Amount" type="number" value={formData.UPIAmount || ''} onChange={(e) => handleChange('UPIAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField fullWidth size="small" label="Card Amount" type="number" value={formData.CardAmount || ''} onChange={(e) => handleChange('CardAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField fullWidth size="small" label="Credit Amount" type="number" value={formData.CreditAmount || ''} disabled inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={{ ...compactInput, '& .MuiInputBase-root': { backgroundColor: '#f0fdf4' } }} />
                  </Grid>

                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''} onChange={(e) => handleChange('Remarks', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>

                  {/* ── Delivery Options — compact inline ── */}
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5,
                      bgcolor: isViewMode ? '#f8fafc' : '#f0f9ff',
                      border: '1px solid', borderColor: isViewMode ? '#e2e8f0' : '#bae6fd',
                      borderRadius: 1, px: 1.5, py: 0.5,
                    }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', mr: 0.5 }}>
                        🚚 Delivery:
                      </Typography>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={!!formData.IsInstallation} onChange={(e) => handleChange('IsInstallation', e.target.checked)} disabled={isViewMode} sx={{ p: '2px', color: '#059669', '&.Mui-checked': { color: '#059669' } }} />}
                        label={<Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#059669' }}>Installation</Typography>}
                        sx={{ m: 0, mr: 1 }}
                      />
                      {!!formData.IsInstallation && (
                        <TextField
                          size="small" type="date" label="Installation Date"
                          value={formData.InstallationDate || ''}
                          onChange={(e) => handleChange('InstallationDate', e.target.value)}
                          disabled={isViewMode}
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.InstallationDate}
                          helperText={errors.InstallationDate || ''}
                          sx={{
                            width: 170, ...compactInput,
                            '& .MuiInputBase-root': { bgcolor: '#f0fdf4', fontSize: '0.78rem' },
                            '& .MuiInputLabel-root': { color: '#059669', fontSize: '0.75rem', '&.Mui-focused': { color: '#059669' } },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: errors.InstallationDate ? '#dc2626' : '#86efac' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: errors.InstallationDate ? '#dc2626' : '#059669' },
                          }}
                        />
                      )}
                      <FormControlLabel
                        control={<Checkbox size="small" checked={!!formData.IsCashOnDelivery} onChange={(e) => handleChange('IsCashOnDelivery', e.target.checked)} disabled={isViewMode} sx={{ p: '2px', color: '#dc2626', '&.Mui-checked': { color: '#dc2626' } }} />}
                        label={<Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>Cash On Delivery</Typography>}
                        sx={{ m: 0, mr: 1 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={!!formData.IsExchange} onChange={(e) => { handleChange('IsExchange', e.target.checked); if (!e.target.checked) { handleChange('ExchangeItemName', ''); handleChange('ExchangeAmount', ''); handleChange('IsOnSite', false); } }} disabled={isViewMode} sx={{ p: '2px', color: '#7c3aed', '&.Mui-checked': { color: '#7c3aed' } }} />}
                        label={<Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed' }}>Exchange</Typography>}
                        sx={{ m: 0, mr: 1 }}
                      />
                      {!!formData.IsExchange && (
                        <>
                          <FormControlLabel
                            control={<Checkbox size="small" checked={!!formData.IsOnSite} onChange={(e) => handleChange('IsOnSite', e.target.checked)} disabled={isViewMode} sx={{ p: '2px', color: '#1d4ed8', '&.Mui-checked': { color: '#1d4ed8' } }} />}
                            label={<Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1d4ed8' }}>On Site</Typography>}
                            sx={{ m: 0, mr: 1 }}
                          />
                          <TextField
                            size="small" label="Exchange Item Name"
                            value={formData.ExchangeItemName || ''}
                            onChange={(e) => handleChange('ExchangeItemName', e.target.value)}
                            disabled={isViewMode}
                            placeholder="Item being exchanged"
                            sx={{ width: 200, ...compactInput, '& .MuiInputBase-root': { bgcolor: '#faf5ff', fontSize: '0.78rem' }, '& .MuiInputLabel-root': { color: '#7c3aed', fontSize: '0.75rem', '&.Mui-focused': { color: '#7c3aed' } }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4b5fd' } }}
                          />
                          <TextField
                            size="small" type="number" label="Exchange Amt"
                            value={formData.ExchangeAmount || ''}
                            onChange={(e) => handleChange('ExchangeAmount', e.target.value)}
                            disabled={isViewMode}
                            inputProps={{ min: 0, style: { textAlign: 'right' } }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '0.72rem', color: '#7c3aed', fontWeight: 700 }}>₹</Typography></InputAdornment> }}
                            error={(() => { const gross = (parseFloat(formData.TotalAmount) || 0) + (parseFloat(formData.ExchangeAmount) || 0); return (parseFloat(formData.ExchangeAmount) || 0) > gross; })()}
                            helperText={(() => { const gross = (parseFloat(formData.TotalAmount) || 0) + (parseFloat(formData.ExchangeAmount) || 0); return (parseFloat(formData.ExchangeAmount) || 0) > gross ? 'Cannot exceed Total Amount' : ''; })()}
                            sx={{ width: 130, ...compactInput, '& .MuiInputBase-root': { bgcolor: '#faf5ff', fontSize: '0.78rem' }, '& .MuiInputLabel-root': { color: '#7c3aed', fontSize: '0.75rem', '&.Mui-focused': { color: '#7c3aed' } }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4b5fd' } }}
                          />
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={8} md={4}>
                    {((parseFloat(formData.UPIAmount) || 0) > 0 || (parseFloat(formData.CardAmount) || 0) > 0) ? (
                      <FormControl fullWidth size="small" sx={{
                        ...compactInput,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#059669',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#059669',
                            borderWidth: '2px',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#059669',
                          fontWeight: 500,
                          '&.Mui-focused': {
                            color: '#059669',
                          }
                        }
                      }}>
                        <InputLabel>Bank *</InputLabel>
                        <Select
                          value={formData.BankId || ''}
                          onChange={(e) => handleChange('BankId', e.target.value)}
                          disabled={isViewMode || loading}
                          label="Bank *"
                          sx={{ fontSize: '0.8rem' }}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                        >
                          {loading ? (
                            <MenuItem value="" disabled sx={{ fontSize: '0.85rem', py: 0.5 }}>
                              Loading banks...
                            </MenuItem>
                          ) : banks && banks.length > 0 ? (
                            banks.map((bank) => (
                              <MenuItem key={bank.MasterId || bank.Id} value={bank.MasterId || bank.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                                {bank.Name || bank.MasterName}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled sx={{ fontSize: '0.85rem', py: 0.5 }}>
                              No banks available
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" sx={{
                          color: '#9ca3af',
                          fontSize: '0.75rem',
                          fontStyle: 'italic'
                        }}>
                          Bank field appears for UPI/Card
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  {/* Audit Info - under Remarks */}
                  {isViewMode && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                        Created by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#059669' }}>{formData.CreatedByName || '-'}</Typography> on{' '}
                        <Typography component="span" variant="caption" sx={{ color: '#10b981' }}>
                          {formData.CreatedDate ? new Date(formData.CreatedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.CreatedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                        Modified by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#16a34a' }}>{formData.ModifiedByName || '-'}</Typography> on{' '}
                        <Typography component="span" variant="caption" sx={{ color: '#15803d' }}>
                          {formData.ModifiedDate ? new Date(formData.ModifiedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.ModifiedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                        </Typography>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Dispatch Details Section */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669', mb: 1, mt: 1, display: 'block' }}>Dispatch Details</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f0fdf4', p: 1.5, borderRadius: 1, mb: 1.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <TextField fullWidth size="small" label="Dispatch From" value={formData.DispatchFrom || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField fullWidth size="small" label="Dispatch Location" value={formData.DispatchLocation || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField fullWidth size="small" label="Dispatch Pincode" value={formData.DispatchPincode || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Dispatch Address 1" value={formData.DispatchAddress1 || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Dispatch Address 2" value={formData.DispatchAddress2 || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                </Grid>
              </Box>

              {/* Shipping Details Section */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669', mb: 1, display: 'block' }}>Shipping Details</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f0fdf4', p: 1.5, borderRadius: 1, mb: 1.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4.5}>
                    <TextField fullWidth size="small" label="Ship To" value={formData.ShipTo || ''} onChange={(e) => handleChange('ShipTo', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={2.5}>
                    <TextField fullWidth size="small" label="Ship GST No" value={formData.ShipGSTNo || ''} onChange={(e) => handleChange('ShipGSTNo', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth size="small" label="Ship Location" value={formData.ShipToLocation || ''} onChange={(e) => handleChange('ShipToLocation', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField fullWidth size="small" label="Ship Pincode" value={formData.ShipToPincode || ''} onChange={(e) => handleChange('ShipToPincode', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Ship To Address 1" value={formData.ShipToAddress1 || ''} onChange={(e) => handleChange('ShipToAddress1', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Ship To Address 2" value={formData.ShipToAddress2 || ''} onChange={(e) => handleChange('ShipToAddress2', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              {/* Vehicle Details Section */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669', mb: 1, display: 'block' }}>Vehicle Details</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f0fdf4', p: 1.5, borderRadius: 1, mb: 1.5 }}>
                <Grid container spacing={2}>
                  {/* Row 1: Vehicle Number and Distance */}

                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Transporter Name" value={formData.TransporterName || ''} onChange={(e) => handleChange('TransporterName', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Vehicle Number" value={formData.VehicleNumber || ''} onChange={(e) => handleChange('VehicleNumber', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>


                  {/* Row 2: Transporter Name (wider) and Transporter GST */}
                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Transporter GST" value={formData.TransporterGST || ''} onChange={(e) => handleChange('TransporterGST', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Distance (KM)" type="number" value={formData.Distance || ''} onChange={(e) => handleChange('Distance', e.target.value)} disabled={isViewMode} inputProps={{ min: 0 }} sx={compactInput} />
                  </Grid>
                </Grid>
              </Box>

              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669', mb: 1, display: 'block' }}>Summary</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f0fdf4', p: 1.5, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Subtotal</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.Amount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Discount</Typography><Typography variant="caption" sx={{ fontWeight: 600 }} color={parseFloat(formData.DiscountAmount) > 0 ? 'error' : 'inherit'}>{parseFloat(formData.DiscountAmount) > 0 ? '-' : ''}{formatCurrency(formData.DiscountAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Taxable</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.TaxableAmount)}</Typography></Box>
                {!isIGSTInvoice() ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>CGST</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.CGSTAmount)}</Typography></Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>SGST</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.SGSTAmount)}</Typography></Box>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>IGST</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.IGSTAmount)}</Typography></Box>
                )}
                {(parseFloat(formData.FreightAmount) || 0) > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Freight</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.FreightAmount)}</Typography></Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Freight GST ({formData.FreightGSTRate || 0}%)</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.FreightGSTAmount)}</Typography></Box>
                  </>
                )}
                {(parseFloat(formData.PackingAmount) || 0) > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Packing</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.PackingAmount)}</Typography></Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Packing GST ({formData.PackingGSTRate || 0}%)</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.PackingGSTAmount)}</Typography></Box>
                  </>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>TCS</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.TCSAmount)}</Typography></Box>
                {formData.IsExchange && (parseFloat(formData.ExchangeAmount) || 0) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#7c3aed' }}>Exchange Deduction</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed' }}>-{formatCurrency(formData.ExchangeAmount)}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Round Off</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formData.RoundOff >= 0 ? '+' : ''}{(formData.RoundOff || 0).toFixed(2)}</Typography></Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#059669' }}>{formatCurrency(formData.TotalAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Total Paid</Typography><Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981' }}>{formatCurrency((parseFloat(formData.CashAmount) || 0) + (parseFloat(formData.UPIAmount) || 0) + (parseFloat(formData.CardAmount) || 0))}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Balance</Typography><Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626' }}>{formatCurrency((parseFloat(formData.TotalAmount) || 0) - ((parseFloat(formData.CashAmount) || 0) + (parseFloat(formData.UPIAmount) || 0) + (parseFloat(formData.CardAmount) || 0)))}</Typography></Box>
              </Box>
            </Grid>
          </Grid>

        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, borderTop: isViewMode ? '1px solid #e2e8f0' : '1px solid #d1fae5', bgcolor: isViewMode ? '#f8fafc' : '#f0fdf4' }}>
          <Button onClick={handleCancelClick} variant="outlined" size="small">Cancel</Button>
          {isViewMode && onPrint && (
            <Button onClick={onPrint} variant="contained" size="small" disabled={formData.IsHold} sx={{ background: formData.IsHold ? '#9ca3af' : '#2C3E6E' }}>
              🖨️ Print Bill
            </Button>
          )}
          {!isViewMode && <Button onClick={handleSaveWithValidation} variant="contained" size="small" disabled={loading} sx={formData.IsHold ? { bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } } : {}}>{formData.IsHold ? '⚠ Hold' : 'Save'}</Button>}
        </DialogActions>
      </Dialog>

      {/* View All Items Modal */}
      <Dialog
        open={itemsModalOpen}
        onClose={handleViewAllItemsClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
            position: 'relative',
            m: 1
          }
        }}
      >

        <DialogTitle sx={{ py: 1, px: 1.5, borderBottom: '1px solid #d1fae5', bgcolor: '#f0fdf4' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#059669', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#064e3b', fontSize: '0.9rem' }}>All Sale Items ({saleDetails.length})</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton onClick={handleViewAllItemsClose} size="small" sx={{ p: 0.4 }}><Close fontSize="small" /></IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem' }}>#</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', color: 'white', fontWeight: 600, fontSize: '0.7rem', minWidth: '200px' }}>Item Name</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '80px' }}>HSN</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '70px' }}>Qty</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '85px' }}>Rate</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '65px' }}>Disc%</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '60px' }}>GST%</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '85px' }}>GST Amt</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '85px' }}>Taxable</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '85px' }}>Total</th>
                  {!isViewMode && <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '50px' }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {saleDetails.map((d, index) => (
                  <tr key={index} style={{ background: index % 2 === 0 ? '#f0fdf4' : '#ffffff', borderBottom: '1px solid #d1fae5' }}>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 500, color: '#059669', fontSize: '0.75rem' }}>{index + 1}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 500, color: '#064e3b' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#064e3b', fontSize: '0.75rem' }}>{d.ItemName || items.find(i => i.Id === parseInt(d.ItemId))?.Name || '-'}</span>
                      ) : (
                        <Select
                          fullWidth
                          size="small"
                          value={d.ItemId || ''}
                          onChange={(e) => handleItemSelect(index, e.target.value)}
                          sx={{
                            fontSize: '0.75rem',
                            minWidth: '180px',
                            '& .MuiSelect-select': { py: 0.4, fontSize: '0.75rem' }
                          }}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.4 } } } }}
                        >
                          <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>Select Item</MenuItem>
                          {items.map(item => {
                            const isAlreadySelected = saleDetails.some((detail, i) =>
                              i !== index && detail.ItemId === item.Id
                            );
                            return (
                              <MenuItem
                                key={item.Id}
                                value={item.Id}
                                disabled={isAlreadySelected}
                                sx={{
                                  fontSize: '0.75rem',
                                  py: 0.2,
                                  opacity: isAlreadySelected ? 0.5 : 1,
                                  color: isAlreadySelected ? '#9ca3af' : 'inherit'
                                }}
                              >
                                {item.Name} {isAlreadySelected && '(Already Added)'}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', color: '#6b7280', fontSize: '0.7rem' }}>{d.HSNCode || '-'}</td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#064e3b' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#064e3b', fontSize: '0.75rem' }}>{d.Quantity || 0}</span>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={d.Quantity ?? ''}
                          onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            min: 0,
                            style: { textAlign: 'center', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }}
                          sx={{
                            width: '65px',
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: '#064e3b' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#064e3b', fontSize: '0.75rem' }}>{formatCurrency(d.Rate)}</span>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={d.Rate ?? ''}
                          onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            min: 0,
                            style: { textAlign: 'right', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }}
                          sx={{
                            width: '80px',
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#059669' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#059669', fontSize: '0.75rem' }}>{d.DiscountPercentage || 0}%</span>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={d.DiscountPercentage ?? 0}
                          onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)}
                          disabled={!d.Quantity || parseFloat(d.Quantity) <= 0}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            min: 0,
                            max: 100,
                            style: { textAlign: 'center', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }}
                          sx={{
                            width: '60px',
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }}
                          title={`Discount percentage (max 100%)`}
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#059669', fontSize: '0.75rem' }}>{d.GSTRate || 0}%</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: '#059669', fontSize: '0.75rem' }}>
                      {isIGSTInvoice() ? formatCurrency(d.IGSTAmount) : formatCurrency((d.CGSTAmount || 0) + (d.SGSTAmount || 0))}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: '#064e3b', fontSize: '0.75rem' }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 700, color: '#059669', fontSize: '0.75rem' }}>{formatCurrency(d.TotalAmount)}</td>
                    {!isViewMode && (
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => removeDetailRow(index)}
                          sx={{ p: 0.2, color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}
                        >
                          <Delete sx={{ fontSize: 14 }} />
                        </IconButton>
                      </td>
                    )}
                  </tr>
                ))}
                {saleDetails.length === 0 && (
                  <tr>
                    <td colSpan={isViewMode ? 10 : 11} style={{ padding: '16px', textAlign: 'center', color: '#6b7280', backgroundColor: '#f0fdf4', fontSize: '0.8rem' }}>
                      No items added
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot style={{ position: 'sticky', bottom: 0, background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' }}>
                <tr style={{ borderTop: '2px solid #059669' }}>
                  <td colSpan="3" style={{ padding: '8px 6px', fontWeight: 700, textAlign: 'right', color: '#064e3b', fontSize: '0.75rem' }}>Totals:</td>
                  <td style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 700, color: '#059669', fontSize: '0.75rem' }}>{saleDetails.reduce((sum, d) => sum + (parseFloat(d.Quantity) || 0), 0)}</td>
                  <td colSpan="4"></td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#064e3b', fontSize: '0.75rem' }}>{formatCurrency(saleDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0))}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#059669', fontSize: '0.75rem' }}>{formatCurrency(saleDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0))}</td>
                  {!isViewMode && <td style={{ padding: '8px 6px' }}></td>}
                </tr>
              </tfoot>
            </table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid #d1fae5', bgcolor: '#f0fdf4', justifyContent: 'flex-end' }}>
          <Button onClick={handleViewAllItemsClose} variant="outlined" size="small" sx={{ fontSize: '0.75rem', py: 0.4, px: 1.5 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Validation Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '8px',
            m: isMobile ? 0 : 1,
            maxHeight: isMobile ? '100vh' : '80vh',
            width: isMobile ? '100%' : '85%',
            maxWidth: '1100px',
            minWidth: isMobile ? 'unset' : '850px'
          }
        }}
      >
        <DialogTitle sx={{
          py: 0.75,
          px: 1.5,
          borderBottom: '1px solid #d1d5db',
          bgcolor: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: '#fff',
          minHeight: '40px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <List sx={{ color: '#fff', fontSize: 16 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#041672ff', fontSize: '0.85rem' }}>
                Get Items ({getFilteredItems().length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small" sx={{ color: '#fff', p: 0.25 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280', fontSize: '0.85rem' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name, HSN code, or item code..."
                  value={getItemsSearchValue}
                  onChange={(e) => handleGetItemsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 16, color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                    endAdornment: getItemsSearchValue && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleGetItemsSearch('')}
                          sx={{ p: 0.25 }}
                        >
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' },
                    '& .MuiOutlinedInput-root': { bgcolor: '#fff' }
                  }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 1.5, p: 1, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 8).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Code ? `${item.Code} - ` : ''}${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    ))}
                    {selectedItems.length > 8 && (
                      <Chip label={`+${selectedItems.length - 8} more`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    )}
                  </Box>
                </Box>
              )}

              {/* Branch Assignment Panel — shown when 1+ items selected */}
              {selectedItems.length >= 1 && (
                <Box sx={{ mb: 1.5, p: 1, bgcolor: branchAssignMode ? '#fef3c7' : '#f0fdf4', borderRadius: 1, border: `1px solid ${branchAssignMode ? '#fcd34d' : '#bbf7d0'}` }}>
                  {/* Header row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: branchAssignMode ? 1 : 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: branchAssignMode ? '#92400e' : '#166534', fontSize: '0.75rem' }}>
                        🏬 Split Item Qty Across Branches
                      </Typography>
                      {branchAssignMode && (
                        <Chip label="Active" size="small" sx={{ bgcolor: '#f59e0b', color: '#fff', fontSize: '0.6rem', height: '18px', fontWeight: 700 }} />
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant={branchAssignMode ? 'contained' : 'outlined'}
                      onClick={() => {
                        setBranchAssignMode(prev => {
                          if (prev) setItemBranchSplits({});
                          return !prev;
                        });
                      }}
                      sx={{
                        fontSize: '0.65rem', py: 0.3, px: 1,
                        bgcolor: branchAssignMode ? '#f59e0b' : undefined,
                        borderColor: branchAssignMode ? '#f59e0b' : '#16a34a',
                        color: branchAssignMode ? '#fff' : '#16a34a',
                        '&:hover': { bgcolor: branchAssignMode ? '#d97706' : '#f0fdf4' }
                      }}
                    >
                      {branchAssignMode ? 'Disable' : 'Enable'}
                    </Button>
                  </Box>

                  {branchAssignMode && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {selectedItems.map(item => {
                        const splits = itemBranchSplits[item.Id] || [];
                        const totalQty = parseFloat(item.selectedQuantity) || 0;
                        const splitTotal = splits.reduce((sum, s) => sum + (parseFloat(s.qty) || 0), 0);
                        const splitQtyOk = splits.length === 0 || Math.abs(splitTotal - totalQty) < 0.001;

                        return (
                          <Box key={item.Id} sx={{ bgcolor: '#fff', border: `1px solid ${!splitQtyOk ? '#fca5a5' : '#e5e7eb'}`, borderRadius: 1, p: 0.75 }}>
                            {/* Item header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>
                                  {item.Name}
                                </Typography>
                                <Chip
                                  label={`Total Qty: ${totalQty}`}
                                  size="small"
                                  sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontSize: '0.6rem', height: '18px', fontWeight: 700 }}
                                />
                                <Chip
                                  label={`Branch Stock: ${parseFloat(item.CurrentBranchStock ?? item.BalanceStock) || 0}`}
                                  size="small"
                                  sx={{
                                    bgcolor: (parseFloat(item.CurrentBranchStock ?? item.BalanceStock) || 0) > 0 ? '#dcfce7' : '#fef2f2',
                                    color: (parseFloat(item.CurrentBranchStock ?? item.BalanceStock) || 0) > 0 ? '#166534' : '#dc2626',
                                    fontSize: '0.6rem', height: '18px', fontWeight: 700
                                  }}
                                />
                                {splits.length > 0 && (
                                  <Chip
                                    label={`Split Total: ${splitTotal}${!splitQtyOk ? ` ≠ ${totalQty}` : ' ✓'}`}
                                    size="small"
                                    sx={{
                                      bgcolor: splitQtyOk ? '#dcfce7' : '#fef2f2',
                                      color: splitQtyOk ? '#166534' : '#dc2626',
                                      fontSize: '0.6rem', height: '18px', fontWeight: 700
                                    }}
                                  />
                                )}
                              </Box>
                              <Button
                                size="small"
                                onClick={() => handleAddSplit(item.Id)}
                                sx={{ fontSize: '0.6rem', py: 0.2, px: 0.75, bgcolor: '#1e40af', color: '#fff', '&:hover': { bgcolor: '#1d4ed8' }, minWidth: 'auto' }}
                              >
                                + Add Branch Split
                              </Button>
                            </Box>

                            {/* Split rows */}
                            {splits.length === 0 ? (
                              <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', fontStyle: 'italic', pl: 0.5 }}>
                                No splits — click "Add Branch Split" to assign qty to a specific branch
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {splits.map((split, si) => {
                                  const splitQty = parseFloat(split.qty) || 0;
                                  const branchName = branchList.find(b => b.Id === parseInt(split.branchId))?.Name || '';
                                  // Aggregate total qty for the same branch to validate stock
                                  const sameBranchTotal = split.branchId
                                    ? splits.filter(sp => sp.branchId === split.branchId).reduce((sum, sp) => sum + (parseFloat(sp.qty) || 0), 0)
                                    : splitQty;
                                  const stockExceeded = split.branchId && !split.loading && split.stock !== null && sameBranchTotal > split.stock;

                                  return (
                                    <Box key={split.splitId} sx={{
                                      display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
                                      bgcolor: stockExceeded || split.error ? '#fef2f2' : '#f8fafc',
                                      border: `1px solid ${stockExceeded || split.error ? '#fca5a5' : '#e2e8f0'}`,
                                      borderRadius: 1, p: 0.5
                                    }}>
                                      {/* Split number */}
                                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', minWidth: 16 }}>
                                        #{si + 1}
                                      </Typography>

                                      {/* Branch dropdown */}
                                      <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel sx={{ fontSize: '0.7rem' }}>Branch *</InputLabel>
                                        <Select
                                          value={split.branchId}
                                          label="Branch *"
                                          onChange={(e) => handleSplitBranchChange(item.Id, split.splitId, e.target.value)}
                                          sx={{ fontSize: '0.7rem', height: '26px', '& .MuiSelect-select': { py: 0.25 } }}
                                          MenuProps={{ PaperProps: { sx: { maxHeight: 180, '& .MuiMenuItem-root': { fontSize: '0.78rem' } } } }}
                                        >
                                          <MenuItem value="" sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                            <em>Select Branch</em>
                                          </MenuItem>
                                          {branchList.map(b => (
                                            <MenuItem key={b.Id} value={b.Id} sx={{ fontSize: '0.75rem' }}>
                                              {b.Name}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>

                                      {/* Qty input */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#374151', fontWeight: 600 }}>Qty:</Typography>
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={split.qty}
                                          onChange={(e) => handleSplitQtyChange(item.Id, split.splitId, e.target.value)}
                                          inputProps={{ min: 0, step: 1 }}
                                          sx={{
                                            width: 56,
                                            '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.72rem', textAlign: 'center', height: '18px' },
                                            '& .MuiOutlinedInput-root': { height: '26px' }
                                          }}
                                        />
                                      </Box>

                                      {/* Branch stock badge */}
                                      {split.branchId && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                          {split.loading ? (
                                            <CircularProgress size={12} />
                                          ) : split.stock !== null ? (
                                            <Chip
                                              label={`Stock: ${split.stock}`}
                                              size="small"
                                              sx={{
                                                bgcolor: stockExceeded ? '#fef2f2' : (split.stock > 0 ? '#dcfce7' : '#fef2f2'),
                                                color: stockExceeded ? '#dc2626' : (split.stock > 0 ? '#166534' : '#dc2626'),
                                                fontSize: '0.6rem', height: '18px', fontWeight: 700,
                                                border: stockExceeded ? '1px solid #fca5a5' : undefined
                                              }}
                                            />
                                          ) : split.error ? (
                                            <Typography sx={{ fontSize: '0.6rem', color: '#dc2626' }}>{split.error}</Typography>
                                          ) : null}
                                          {stockExceeded && (
                                            <Typography sx={{ fontSize: '0.6rem', color: '#dc2626', fontWeight: 700 }}>
                                              ⚠ Exceeds!
                                            </Typography>
                                          )}
                                        </Box>
                                      )}

                                      {/* Remove split */}
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemoveSplit(item.Id, split.splitId)}
                                        sx={{ p: 0.2, color: '#dc2626', ml: 'auto' }}
                                      >
                                        <Delete sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Items Table - Desktop */}
              {!isMobile && (
                <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                  <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ background: 'linear-gradient(135deg, #166534 0%, #22c55e 100%)', height: '32px' }}>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '4%', fontSize: '0.65rem' }}>
                            <Checkbox
                              size="small"
                              checked={selectedItems.length === getPaginatedItems().length && getPaginatedItems().length > 0}
                              indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems().length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  getPaginatedItems().forEach(item => {
                                    if (!selectedItems.find(si => si.Id === item.Id)) {
                                      handleItemSelection(item, true);
                                    }
                                  });
                                } else {
                                  getPaginatedItems().forEach(item => {
                                    handleItemSelection(item, false);
                                  });
                                }
                              }}
                              sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, '& .MuiSvgIcon-root': { fontSize: 14 } }}
                            />
                          </th>
                          <th style={{ padding: '4px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '28%', fontSize: '0.65rem' }}>Item</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem' }}>HSN</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%', fontSize: '0.65rem' }}>GST%</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Stock (Branch / Co.)</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem' }}>Qty</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>Rate</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%', fontSize: '0.65rem' }}>Disc%</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '9%', fontSize: '0.65rem' }}>Disc Amt</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedItems().map((item, index) => {
                          const selectedItem = selectedItems.find(si => si.Id === item.Id);
                          const isSelected = !!selectedItem;
                          const isAlreadyAdded = isItemAlreadyInSale(item.Id);

                          return (
                            <tr key={item.Id} style={{
                              borderBottom: '1px solid #e5e7eb',
                              background: isSelected ? '#eff6ff' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                              opacity: isAlreadyAdded ? 0.5 : 1
                            }}>
                              <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  disabled={isAlreadyAdded}
                                  onChange={(e) => handleItemSelection(item, e.target.checked)}
                                  sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }}
                                />
                              </td>
                              <td style={{ padding: '3px 8px', fontWeight: 500, fontSize: '0.75rem' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <span>{`${item.Name}`}</span>
                                  {item.IsRawMaterial && (
                                    <Chip label="Raw Material" variant="filled" size="small" sx={{ bgcolor: '#f59e0b !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                                  )}
                                  {item.IsTradingItem && (
                                    <Chip label="Trading" variant="filled" size="small" sx={{ bgcolor: '#2563eb !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                                  )}
                                  {item.IsProduct && (
                                    <Chip label="Product" variant="filled" size="small" sx={{ bgcolor: '#059669 !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                                  )}
                                  {item.IsBundle && (
                                    <Chip label="Bundle" variant="filled" size="small" sx={{ bgcolor: '#7c3aed !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                                  )}
                                  {item.IsService && (
                                    <Chip label="Service" variant="filled" size="small" sx={{ bgcolor: '#db2777 !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                                  )}
                                  {isAlreadyAdded && (
                                    <Chip label="Added" size="small" color="success" sx={{ fontSize: '0.6rem', height: 14 }} />
                                  )}
                                </Box>
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.7rem', color: '#6b7280' }}>{item.HSNCode || '-'}</td>
                              <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#1e40af' }}>{item.GSTRate || 0}%</td>
                              <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.65rem' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                    <span style={{ fontWeight: 700, color: (item.CurrentBranchStock || 0) > 0 ? '#059669' : '#dc2626' }}>
                                      {item.CurrentBranchStock || 0}
                                    </span>
                                    <span style={{ color: '#9ca3af' }}>/</span>
                                    <span style={{ fontWeight: 600, color: '#1e40af' }}>
                                      {item.CompanyStock || 0}
                                    </span>
                                  </Box>
                                  {/* Branch-wise stock breakdown */}
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, justifyContent: 'center' }}>
                                    {item.CHR != null && item.CHR > 0 && (
                                      <span style={{ fontSize: '0.55rem', color: '#6b7280', bgcolor: '#f3f4f6', padding: '0 2px', borderRadius: 2 }}>CHR:{item.CHR}</span>
                                    )}
                                    {item.NPR != null && item.NPR > 0 && (
                                      <span style={{ fontSize: '0.55rem', color: '#6b7280', padding: '0 2px', borderRadius: 2 }}>NPR:{item.NPR}</span>
                                    )}
                                    {item.GD01 != null && item.GD01 > 0 && (
                                      <span style={{ fontSize: '0.55rem', color: '#6b7280', padding: '0 2px', borderRadius: 2 }}>GD01:{item.GD01}</span>
                                    )}
                                    {item.GD02 != null && item.GD02 > 0 && (
                                      <span style={{ fontSize: '0.55rem', color: '#6b7280', padding: '0 2px', borderRadius: 2 }}>GD02:{item.GD02}</span>
                                    )}
                                    {item.GD03 != null && item.GD03 > 0 && (
                                      <span style={{ fontSize: '0.55rem', color: '#6b7280', padding: '0 2px', borderRadius: 2 }}>GD03:{item.GD03}</span>
                                    )}
                                  </Box>
                                </Box>
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                {isSelected ? (
                                  <Box>
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={selectedItem.selectedQuantity}
                                      onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                      inputProps={{ min: 1 }}
                                      error={!!selectedItem.stockError}
                                      sx={{
                                        width: 50,
                                        '& .MuiInputBase-input': {
                                          py: 0.2,
                                          px: 0.3,
                                          fontSize: '0.7rem',
                                          textAlign: 'center',
                                          height: '16px'
                                        },
                                        '& .MuiOutlinedInput-root': { height: '24px' }
                                      }}
                                    />
                                    {selectedItem.stockError && (
                                      <Typography sx={{ fontSize: '0.6rem', color: '#dc2626', lineHeight: 1, mt: 0.25 }}>{selectedItem.stockError}</Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>-</span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedRate}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                    inputProps={{ min: parseFloat(item.MinRate) || 0, max: parseFloat(item.MaxRate) || undefined }}
                                    title={item.MinRate || item.MaxRate ? `Min: ${item.MinRate || 0} | Max: ${item.MaxRate || '∞'}` : ''}
                                    sx={{
                                      width: 65,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'right',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>
                                    ₹{(item.MaxRate || item.SaleRate || 0).toFixed(2)}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedDiscountPercentage}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountPercentage', e.target.value)}
                                    disabled={!selectedItem.selectedQuantity || parseFloat(selectedItem.selectedQuantity) <= 0}
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={{
                                      width: 45,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'center',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                    title={`Discount percentage (max 100%)`}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>0</span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedDiscountAmount}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountAmount', e.target.value)}
                                    disabled={!selectedItem.selectedQuantity || parseFloat(selectedItem.selectedQuantity) <= 0}
                                    inputProps={{ min: 0 }}
                                    sx={{
                                      width: 60,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'right',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                    title={`Discount amount (max ₹${((selectedItem.selectedQuantity || 0) * (selectedItem.selectedRate || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of item amount)`}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>-</span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                                {isSelected ? (
                                  (() => {
                                    const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                    const rate = parseFloat(selectedItem.selectedRate) || 0;
                                    const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                    const discAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
                                    const gstRate = item.GSTRate || 0;
                                    if (isIncludingTax()) {
                                      // Rate already includes GST — total = qty × rate (minus discount on base)
                                      const totalWithGst = qty * rate;
                                      const base = totalWithGst / (1 + gstRate / 100);
                                      const finalDiscAmt = discAmt > 0 ? discAmt : (base * discPct) / 100;
                                      const taxable = base - finalDiscAmt;
                                      const gstAmt = (taxable * gstRate) / 100;
                                      return `₹${(taxable + gstAmt).toFixed(2)}`;
                                    } else {
                                      const amount = qty * rate;
                                      const finalDiscAmt = discAmt > 0 ? discAmt : (amount * discPct) / 100;
                                      const taxable = amount - finalDiscAmt;
                                      const gstAmt = (taxable * gstRate) / 100;
                                      return `₹${(taxable + gstAmt).toFixed(2)}`;
                                    }
                                  })()
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {getPaginatedItems().length === 0 && (
                          <tr>
                            <td colSpan="10" style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '0.8rem' }}>
                              {getItemsSearchValue ? 'No items found matching your search' : 'No items available'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}

              {/* Items Cards - Mobile */}
              {isMobile && (
                <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
                  {getPaginatedItems().map((item, index) => {
                    const selectedItem = selectedItems.find(si => si.Id === item.Id);
                    const isSelected = !!selectedItem;
                    const isAlreadyAdded = isItemAlreadyInSale(item.Id);

                    return (
                      <Box
                        key={item.Id}
                        sx={{
                          border: isSelected ? '2px solid #1e40af' : '1px solid #e5e7eb',
                          borderRadius: 1.5,
                          p: 1,
                          mb: 0.75,
                          bgcolor: isSelected ? '#eff6ff' : '#fff',
                          opacity: isAlreadyAdded ? 0.6 : 1,
                          boxShadow: isSelected ? '0 2px 8px rgba(30, 64, 175, 0.12)' : '0 1px 2px rgba(0,0,0,0.06)',
                          transition: 'all 0.15s ease',
                          position: 'relative'
                        }}
                        onClick={() => !isAlreadyAdded && handleItemSelection(item, !isSelected)}
                      >
                        {/* Selection Indicator */}
                        {isSelected && (
                          <Box sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: '#1e40af',
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <CheckCircle sx={{ fontSize: 14, color: '#fff' }} />
                          </Box>
                        )}

                        {/* Already Added Badge */}
                        {isAlreadyAdded && (
                          <Box sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: '#059669',
                            borderRadius: 1,
                            px: 1,
                            py: 0.25
                          }}>
                            <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 600 }}>
                              ADDED
                            </Typography>
                          </Box>
                        )}

                        {/* Item Info */}
                        <Box sx={{ pr: 3 }}>
                          <Typography variant="body1" sx={{
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            color: '#1e293b',
                            mb: 0.25,
                            lineHeight: 1.3
                          }}>
                            {item.Code ? `${item.Code} - ${item.Name}` : item.Name}
                          </Typography>

                          {/* Item Type Chips */}
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                            {item.IsRawMaterial && (
                              <Chip label="Raw Material" variant="filled" size="small" sx={{ bgcolor: '#f59e0b !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                            )}
                            {item.IsTradingItem && (
                              <Chip label="Trading" variant="filled" size="small" sx={{ bgcolor: '#2563eb !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                            )}
                            {item.IsProduct && (
                              <Chip label="Product" variant="filled" size="small" sx={{ bgcolor: '#059669 !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                            )}
                            {item.IsBundle && (
                              <Chip label="Bundle" variant="filled" size="small" sx={{ bgcolor: '#7c3aed !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                            )}
                            {item.IsService && (
                              <Chip label="Service" variant="filled" size="small" sx={{ bgcolor: '#db2777 !important', color: '#fff !important', fontSize: '0.55rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                            )}
                          </Box>

                          {/* Stock and Rate Row */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 500 }}>
                                Branch:
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: (item.CurrentBranchStock || 0) > 0 ? '#059669' : '#dc2626',
                                bgcolor: (item.CurrentBranchStock || 0) > 0 ? '#f0fdf4' : '#fef2f2',
                                px: 0.75,
                                py: 0.15,
                                borderRadius: 1
                              }}>
                                {item.CurrentBranchStock || 0}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 500 }}>
                                Co:
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: '#1e40af',
                                bgcolor: '#eff6ff',
                                px: 0.75,
                                py: 0.15,
                                borderRadius: 1
                              }}>
                                {item.CompanyStock || 0}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                fontWeight: 500
                              }}>
                                Rate:
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: '#1e40af',
                                bgcolor: '#eff6ff',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1
                              }}>
                                ₹{(item.SaleRate || 0).toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Branch-wise Stock Breakdown */}
                          {(item.CHR > 0 || item.NPR > 0 || item.GD01 > 0 || item.GD02 > 0 || item.GD03 > 0) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                              {item.CHR > 0 && (
                                <Chip label={`CHR: ${item.CHR}`} size="small" sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                              )}
                              {item.NPR > 0 && (
                                <Chip label={`NPR: ${item.NPR}`} size="small" sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                              )}
                              {item.GD01 > 0 && (
                                <Chip label={`GD01: ${item.GD01}`} size="small" sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                              )}
                              {item.GD02 > 0 && (
                                <Chip label={`GD02: ${item.GD02}`} size="small" sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#fce7f3', color: '#9d174d', fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                              )}
                              {item.GD03 > 0 && (
                                <Chip label={`GD03: ${item.GD03}`} size="small" sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#f3e8ff', color: '#6b21a8', fontWeight: 600, '& .MuiChip-label': { px: 0.5 } }} />
                              )}
                            </Box>
                          )}

                          {/* Additional Info Row */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{
                              fontSize: '0.7rem',
                              color: '#9ca3af',
                              bgcolor: '#f3f4f6',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1
                            }}>
                              HSN: {item.HSNCode || 'N/A'}
                            </Typography>

                            <Typography sx={{
                              fontSize: '0.7rem',
                              color: '#7c3aed',
                              bgcolor: '#f3e8ff',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontWeight: 600
                            }}>
                              GST: {item.GSTRate || 0}%
                            </Typography>
                          </Box>

                          {/* Quantity Input - Only show when selected */}
                          {isSelected && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e5e7eb' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: '#374151',
                                  minWidth: '60px'
                                }}>
                                  Quantity:
                                </Typography>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  inputProps={{ min: 1 }}
                                  sx={{
                                    flex: 1,
                                    '& .MuiInputBase-input': {
                                      fontSize: '0.8rem',
                                      textAlign: 'center',
                                      fontWeight: 600,
                                      color: '#1e40af'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: '#fff',
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                />
                              </Box>

                              {/* Total Display */}
                              <Box sx={{
                                bgcolor: '#1e40af',
                                borderRadius: 1,
                                p: 1,
                                textAlign: 'center'
                              }}>
                                <Typography sx={{
                                  fontSize: '0.7rem',
                                  color: '#bfdbfe',
                                  mb: 0.25
                                }}>
                                  Total Amount
                                </Typography>
                                <Typography sx={{
                                  fontSize: '0.9rem',
                                  fontWeight: 700,
                                  color: '#fff'
                                }}>
                                  ₹{(() => {
                                    const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                    const rate = parseFloat(selectedItem.selectedRate) || 0;
                                    const gstRate = item.GSTRate || 0;
                                    if (isIncludingTax()) {
                                      // Rate already includes GST — total = qty × rate
                                      const totalWithGst = qty * rate;
                                      const base = totalWithGst / (1 + gstRate / 100);
                                      const gstAmt = (base * gstRate) / 100;
                                      return (base + gstAmt).toFixed(2);
                                    } else {
                                      const amount = qty * rate;
                                      const gstAmt = (amount * gstRate) / 100;
                                      return (amount + gstAmt).toFixed(2);
                                    }
                                  })()}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}

                  {getPaginatedItems().length === 0 && (
                    <Box sx={{
                      textAlign: 'center',
                      py: 6,
                      color: '#6b7280',
                      bgcolor: '#f9fafb',
                      borderRadius: 2,
                      border: '1px dashed #d1d5db'
                    }}>
                      <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 1 }}>
                        {getItemsSearchValue ? '🔍 No items found' : '📦 No items available'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {getItemsSearchValue ? 'Try adjusting your search terms' : 'No items to display'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Pagination */}
              {getTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                  <Pagination
                    count={getTotalPages}
                    page={currentItemsPage}
                    onChange={(e, page) => setCurrentItemsPage(page)}
                    size="small"
                    color="primary"
                    sx={{ '& .MuiPaginationItem-root': { fontSize: '0.75rem' } }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid #d1d5db', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
            {selectedItems.length > 0 && `${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''} selected`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleCloseGetItemsModal}
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedItems}
              variant="contained"
              size="small"
              disabled={selectedItems.length === 0 || addingItemsLoading}
              sx={{
                bgcolor: '#1e40af',
                '&:hover': { bgcolor: '#1d4ed8' },
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5
              }}
            >
              {addingItemsLoading ? (
                <>
                  <CircularProgress size={14} sx={{ mr: 0.5 }} />
                  Adding...
                </>
              ) : (
                `Add Selected (${selectedItems.length})`
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Branch Split Modal */}
      <Dialog
        open={branchSplitModalOpen}
        onClose={handleCloseBranchSplitModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e9d5ff', bgcolor: '#7c3aed' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>
              🏬 Branch Split — Assign Item Qty to Branches
            </Typography>
            <IconButton onClick={handleCloseBranchSplitModal} size="small" sx={{ color: '#fff', p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 1.5, bgcolor: '#faf5ff' }}>
          {branchListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
              <Typography sx={{ ml: 2, fontSize: '0.85rem', color: '#6b7280' }}>Loading branches...</Typography>
            </Box>
          ) : saleDetails.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
              <Typography sx={{ fontSize: '0.85rem' }}>No items in sale. Add items first.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {saleDetails.map(d => {
                const key = d._rowKey;
                if (!key || !parseInt(d.ItemId)) return null;
                const splits = splitMap[key] || [];
                const totalQty = parseFloat(d.Quantity) || 0;
                const splitTotal = splits.reduce((sum, s) => sum + (parseFloat(s.qty) || 0), 0);
                const splitQtyOk = splits.length === 0 || Math.abs(splitTotal - totalQty) < 0.001;
                const defaultStock = parseFloat(d.BalanceStock) || 0;
                // Get branch-wise stock from items data
                const itemStockData = items.find(i => i.Id === parseInt(d.ItemId));

                return (
                  <Box key={key} sx={{
                    bgcolor: defaultStock === 0 ? '#fff7f7' : (!splitQtyOk && splits.length > 0 ? '#fffbeb' : '#fff'),
                    border: `1px solid ${
                      defaultStock === 0 ? '#fca5a5' :
                      (!splitQtyOk && splits.length > 0 ? '#fcd34d' : '#e9d5ff')
                    }`,
                    borderRadius: 1.5, p: 1.25
                  }}>
                    {/* Item header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>
                          {d.ItemName || `Item #${d.ItemId}`}
                        </Typography>
                        <Chip label={`Total Qty: ${totalQty}`} size="small"
                          sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontSize: '0.6rem', height: 18, fontWeight: 700 }} />
                        <Chip label={`Branch Stock: ${defaultStock}`} size="small"
                          sx={{ bgcolor: defaultStock > 0 ? '#dcfce7' : '#fef2f2', color: defaultStock > 0 ? '#166534' : '#dc2626', fontSize: '0.6rem', height: 18, fontWeight: 700 }} />
                        {itemStockData?.CompanyStock > 0 && (
                          <Chip label={`Co: ${itemStockData.CompanyStock}`} size="small"
                            sx={{ bgcolor: '#eff6ff', color: '#1e40af', fontSize: '0.6rem', height: 18, fontWeight: 700 }} />
                        )}
                        {itemStockData?.CHR > 0 && (
                          <Chip label={`CHR: ${itemStockData.CHR}`} size="small"
                            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: '0.55rem', height: 16, fontWeight: 600 }} />
                        )}
                        {itemStockData?.NPR > 0 && (
                          <Chip label={`NPR: ${itemStockData.NPR}`} size="small"
                            sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontSize: '0.55rem', height: 16, fontWeight: 600 }} />
                        )}
                        {itemStockData?.GD01 > 0 && (
                          <Chip label={`GD01: ${itemStockData.GD01}`} size="small"
                            sx={{ bgcolor: '#d1fae5', color: '#065f46', fontSize: '0.55rem', height: 16, fontWeight: 600 }} />
                        )}
                        {itemStockData?.GD02 > 0 && (
                          <Chip label={`GD02: ${itemStockData.GD02}`} size="small"
                            sx={{ bgcolor: '#fce7f3', color: '#9d174d', fontSize: '0.55rem', height: 16, fontWeight: 600 }} />
                        )}
                        {itemStockData?.GD03 > 0 && (
                          <Chip label={`GD03: ${itemStockData.GD03}`} size="small"
                            sx={{ bgcolor: '#f3e8ff', color: '#6b21a8', fontSize: '0.55rem', height: 16, fontWeight: 600 }} />
                        )}
                        {splits.length > 0 && (
                          <Chip
                            label={`Split: ${splitTotal}${splitQtyOk ? ' ✓' : ` ≠ ${totalQty}`}`}
                            size="small"
                            sx={{ bgcolor: splitQtyOk ? '#dcfce7' : '#fef2f2', color: splitQtyOk ? '#166534' : '#dc2626', fontSize: '0.6rem', height: 18, fontWeight: 700 }}
                          />
                        )}
                      </Box>
                      <Button size="small" onClick={() => handleAddSplitRow(key)}
                        sx={{ fontSize: '0.65rem', py: 0.25, px: 1, bgcolor: '#7c3aed', color: '#fff', '&:hover': { bgcolor: '#6d28d9' }, minWidth: 'auto' }}>
                        + Add Branch
                      </Button>
                    </Box>

                    {/* Split rows */}
                    {splits.length === 0 ? (
                      <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af', fontStyle: 'italic', pl: 0.5 }}>
                        Click "+ Add Branch" to split this item across branches
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {splits.map((s, si) => {
                          const splitQty = parseFloat(s.qty) || 0;
                          const bName = branchList.find(b => b.Id === parseInt(s.branchId))?.Name || '';
                          // Aggregate total qty for the same branch to validate stock
                          const sameBranchTotal = s.branchId
                            ? splits.filter(sp => sp.branchId === s.branchId).reduce((sum, sp) => sum + (parseFloat(sp.qty) || 0), 0)
                            : splitQty;
                          const exceeded = s.branchId && !s.loading && s.stock !== null && sameBranchTotal > s.stock;

                          return (
                            <Box key={s.splitId} sx={{
                              display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
                              bgcolor: exceeded ? '#fee2e2' : (s.stock === 0 && s.stock !== null && !s.loading ? '#fff7f7' : (s.error ? '#fef2f2' : '#f5f3ff')),
                              border: `1px solid ${exceeded ? '#f87171' : (s.stock === 0 && s.stock !== null && !s.loading ? '#fca5a5' : (s.error ? '#fca5a5' : '#ddd6fe'))}`,
                              borderRadius: 1, p: 0.6
                            }}>
                              {/* Row number */}
                              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#7c3aed', minWidth: 18 }}>
                                #{si + 1}
                              </Typography>

                              {/* Branch select */}
                              <FormControl size="small" sx={{ minWidth: 170 }}>
                                <InputLabel sx={{ fontSize: '0.72rem' }}>Branch *</InputLabel>
                                <Select
                                  value={s.branchId}
                                  label="Branch *"
                                  onChange={(e) => handleSplitRowBranchChange(key, s.splitId, e.target.value, d.ItemId)}
                                  sx={{ fontSize: '0.72rem', height: 28, '& .MuiSelect-select': { py: 0.3 } }}
                                  MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.78rem' } } } }}
                                >
                                  <MenuItem value=""><em style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Select Branch</em></MenuItem>
                                  {branchList.map(b => (
                                    <MenuItem key={b.Id} value={b.Id} sx={{ fontSize: '0.75rem' }}>{b.Name}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              {/* Qty */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <Typography sx={{ fontSize: '0.65rem', color: '#374151', fontWeight: 600 }}>Qty:</Typography>
                                <TextField size="small" type="number"
                                  value={s.qty}
                                  onChange={(e) => handleSplitRowQtyChange(key, s.splitId, e.target.value)}
                                  inputProps={{ min: 0, step: 1 }}
                                  sx={{
                                    width: 60,
                                    '& .MuiInputBase-input': { py: 0.3, px: 0.5, fontSize: '0.72rem', textAlign: 'center', height: 18 },
                                    '& .MuiOutlinedInput-root': { height: 28 }
                                  }}
                                />
                              </Box>

                              {/* Branch stock */}
                              {s.branchId && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                  {s.loading ? (
                                    <CircularProgress size={12} sx={{ color: '#7c3aed' }} />
                                  ) : s.stock !== null ? (
                                    <Chip
                                      label={`Stock: ${s.stock}`}
                                      size="small"
                                      sx={{
                                        bgcolor: exceeded ? '#fee2e2' : (s.stock > 0 ? '#dcfce7' : '#fee2e2'),
                                        color: exceeded ? '#dc2626' : (s.stock > 0 ? '#166534' : '#dc2626'),
                                        fontSize: '0.6rem', height: 18, fontWeight: 700,
                                        border: (exceeded || s.stock === 0) ? '1px solid #fca5a5' : '1px solid #86efac'
                                      }}
                                    />
                                  ) : s.error ? (
                                    <Typography sx={{ fontSize: '0.6rem', color: '#dc2626' }}>{s.error}</Typography>
                                  ) : null}
                                  {exceeded && (
                                    <Typography sx={{ fontSize: '0.6rem', color: '#dc2626', fontWeight: 700 }}>⚠ Exceeds!</Typography>
                                  )}
                                  {!exceeded && s.stock === 0 && s.stock !== null && !s.loading && (
                                    <Typography sx={{ fontSize: '0.6rem', color: '#dc2626', fontWeight: 700 }}>⊘ No Stock</Typography>
                                  )}
                                </Box>
                              )}

                              {/* Remove */}
                              <IconButton size="small" onClick={() => handleRemoveSplitRow(key, s.splitId)}
                                sx={{ p: 0.25, color: '#dc2626', ml: 'auto' }}>
                                <Delete sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1, borderTop: '1px solid #e9d5ff', bgcolor: '#faf5ff', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
            Each split row becomes a separate sale line
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCloseBranchSplitModal} variant="outlined" size="small"
              sx={{ fontSize: '0.75rem', borderColor: '#7c3aed', color: '#7c3aed' }}>
              Cancel
            </Button>
            <Button onClick={handleConfirmBranchSplit} variant="contained" size="small"
              sx={{ fontSize: '0.75rem', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
              Apply Splits
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleStayOnPage}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              backgroundColor: '#fef3c7',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            You have unsaved changes that will be lost if you leave this page.
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Are you sure you want to exit without saving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleStayOnPage}
            variant="outlined"
            sx={{
              borderColor: '#10b981',
              color: '#10b981',
              '&:hover': { borderColor: '#059669', backgroundColor: '#f0fdf4' }
            }}
          >
            Stay on Page
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Exit Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hold Save Confirmation Modal */}
      <Dialog
        open={holdConfirmOpen}
        onClose={() => setHoldConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Sale on Hold</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            This sale is marked as <strong style={{ color: '#dc2626' }}>HOLD</strong> because {(() => {
              if (ledgerGroupData && parseFloat(ledgerGroupData.CreditLimit) > 0) {
                const creditLimit = parseFloat(ledgerGroupData.CreditLimit);
                const ledgerBal = customerClosingBalance !== null ? parseFloat(customerClosingBalance) || 0 : 0;
                const effLimit = Math.max(0, creditLimit + ledgerBal);
                const creditAmt = parseFloat(formData.CreditAmount) || 0;
                if (effLimit > 0 && creditAmt > effLimit) {
                  return `the Credit Amount (₹${creditAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}) exceeds the Effective Credit Limit (₹${effLimit.toLocaleString('en-IN', { minimumFractionDigits: 2 })})${ledgerBal !== 0 ? ` [Limit: ₹${creditLimit.toLocaleString('en-IN', { minimumFractionDigits: 2 })} ${ledgerBal >= 0 ? '+' : '-'} Ledger Bal: ₹${Math.abs(ledgerBal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}]` : ''}`;
                }
              }
              return 'the customer has exceeded their credit limit or credit days';
            })()}.
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1.5, fontWeight: 500 }}>
            Do you still want to save this sale?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setHoldConfirmOpen(false)}
            variant="outlined"
            size="small"
            sx={{ borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const stockErrors = getStockErrors();
              if (stockErrors.length > 0) {
                setHoldConfirmOpen(false);
                showError(stockErrors.join(' | '));
                return;
              }
              setHoldConfirmOpen(false);
              onSave();
            }}
            variant="contained"
            size="small"
            sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ledger Modal */}
      <Dialog
        open={ledgerModalOpen}
        onClose={handleCloseLedgerModal}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '8px',
            m: isMobile ? 0 : 1,
            maxHeight: '85vh',
            width: isMobile ? '100%' : '90%',
            maxWidth: '1200px'
          }
        }}
      >
        <DialogTitle sx={{
          py: 1,
          px: 2,
          borderBottom: '1px solid #d1d5db',
          bgcolor: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: '#fff'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#600404ff', fontSize: 18 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#520404ff' }}>
                Customer Ledger - {getCustomerDisplayName() || 'No Customer Selected'}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseLedgerModal} size="small" sx={{ color: '#fff' }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, bgcolor: '#f8fafc' }}>
          {/* Date Range + Account Summary — single row */}
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

              {/* From Date */}
              <TextField
                size="small"
                type="date"
                label="From Date"
                value={ledgerFromDate}
                onChange={(e) => setLedgerFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.6 } }}
              />

              {/* To Date */}
              <TextField
                size="small"
                type="date"
                label="To Date"
                value={ledgerToDate}
                onChange={(e) => setLedgerToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.6 } }}
              />

              {/* Get Ledger Button */}
              <Button
                variant="contained"
                onClick={handleLedgerDateChange}
                disabled={!ledgerFromDate || !ledgerToDate || ledgerLoading}
                sx={{ bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, fontSize: '0.8rem', py: 0.6, whiteSpace: 'nowrap' }}
              >
                {ledgerLoading ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
                Get Ledger
              </Button>

              {/* Divider */}
              {ledgerGroupData && <Box sx={{ width: '1px', height: 36, bgcolor: '#e5e7eb', mx: 0.5 }} />}

              {/* Account Summary chips */}
              {ledgerGroupData && (() => {
                const closingBal = parseFloat(ledgerGroupData.ClosingBalance) || 0;
                const creditLimit = parseFloat(ledgerGroupData.CreditLimit) || 0;
                const creditDays = parseInt(ledgerGroupData.CreditDays) || 0;
                const maxPendingDays = parseInt(ledgerGroupData.MaxPendingDays) || 0;
                const oldestPendingDate = ledgerGroupData.OldestPendingBillDate;
                const daysExceeded = creditDays > 0 && maxPendingDays > 0 && maxPendingDays > creditDays;
                // Only check limit when balance is Debit (customer owes money, closingBal < 0)
                const limitExceeded = creditLimit > 0 && closingBal < 0 && Math.abs(closingBal) > creditLimit;

                const chip = (label, value, bgColor, borderColor, textColor, warn) => (
                  <Box sx={{ px: 1.2, py: 0.5, borderRadius: 1, bgcolor: bgColor, border: `1px solid ${borderColor}`, textAlign: 'center', minWidth: 90 }}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: 1.2 }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: textColor, lineHeight: 1.4 }}>{value}</Typography>
                    {warn && <Typography sx={{ fontSize: '0.6rem', color: '#dc2626', fontWeight: 600, lineHeight: 1 }}>⚠ Exceeded</Typography>}
                  </Box>
                );

                return (
                  <>
                    {chip(
                      'Group Closing Bal',
                      `₹${Math.abs(closingBal).toLocaleString('en-IN', { minimumFractionDigits: 2 })} ${closingBal < 0 ? '(Dr)' : '(Cr)'}`,
                      closingBal < 0 ? '#fef2f2' : '#f0fdf4',
                      closingBal < 0 ? '#fecaca' : '#bbf7d0',
                      closingBal < 0 ? '#dc2626' : '#059669',
                      false
                    )}
                    {chip(
                      'Credit Limit',
                      creditLimit > 0 ? `₹${creditLimit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'No Limit',
                      limitExceeded ? '#fef2f2' : '#f0f9ff',
                      limitExceeded ? '#fecaca' : '#bae6fd',
                      limitExceeded ? '#dc2626' : '#0369a1',
                      limitExceeded
                    )}
                    {chip(
                      'Credit Days',
                      creditDays > 0 ? `${creditDays} days` : 'No Limit',
                      daysExceeded ? '#fef2f2' : '#fefce8',
                      daysExceeded ? '#fecaca' : '#fde68a',
                      daysExceeded ? '#dc2626' : '#92400e',
                      false
                    )}
                    {chip(
                      'Oldest Pending Bill',
                      oldestPendingDate ? new Date(oldestPendingDate).toLocaleDateString('en-IN') : 'None',
                      daysExceeded ? '#fef2f2' : '#f5f3ff',
                      daysExceeded ? '#fecaca' : '#ddd6fe',
                      daysExceeded ? '#dc2626' : '#6d28d9',
                      false
                    )}
                    {chip(
                      'Max Pending Days',
                      maxPendingDays > 0 ? `${maxPendingDays} days` : '0 days',
                      daysExceeded ? '#fef2f2' : '#fff7ed',
                      daysExceeded ? '#fecaca' : '#fed7aa',
                      daysExceeded ? '#dc2626' : '#c2410c',
                      daysExceeded
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>

          {/* Ledger Table */}
          {ledgerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>Loading ledger data...</Typography>
            </Box>
          ) : (
            <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%' }}>Sl No</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '10%' }}>Date</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '32%' }}>Description</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '13%' }}>Debit</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '13%' }}>Credit</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '14%' }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let runningBalance = 0;
                      return ledgerData.map((ledger, index) => {
                        const debitAmount = parseFloat(ledger.debit) || 0;
                        const creditAmount = parseFloat(ledger.credit) || 0;

                        // Check for special entries
                        const description = (ledger.description1 || '').toLowerCase();
                        const isTotal = description.includes('total');
                        const isClosingBalance = description.includes('closing balance');

                        // Calculate running balance for all entries except "Total"
                        if (!isTotal) {
                          runningBalance += creditAmount - debitAmount;
                        }

                        return (
                          <tr key={ledger.id || index} style={{
                            borderBottom: '1px solid #e5e7eb',
                            background: index % 2 === 0 ? '#fff' : '#f9fafb'
                          }}>
                            <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 500, color: '#6b7280' }}>
                              {index + 1}
                            </td>
                            <td style={{ padding: '8px 12px', fontWeight: 500, color: '#374151' }}>
                              {ledger.date1 ? new Date(ledger.date1).toLocaleDateString('en-IN') : '-'}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#374151' }}>
                              {ledger.description1 || '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>
                              {debitAmount > 0 ? `₹${debitAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                              {creditAmount > 0 ? `₹${creditAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                            </td>
                            <td style={{
                              padding: '8px 12px',
                              textAlign: 'right',
                              fontWeight: 700,
                              color: (isTotal || isClosingBalance) ? '#6b7280' : (runningBalance >= 0 ? '#059669' : '#dc2626'),
                              backgroundColor: (isTotal || isClosingBalance) ? 'transparent' : (runningBalance >= 0 ? '#f0fdf4' : '#fef2f2')
                            }}>
                              {(isTotal || isClosingBalance) ? '-' : (
                                `₹${Math.abs(runningBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${runningBalance < 0 ? ' (Dr)' : ' (Cr)'}`
                              )}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                    {ledgerData.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
                          {ledgerFromDate && ledgerToDate ? 'No ledger entries found for the selected date range' : 'Select date range to view ledger entries'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {ledgerData.length > 0 && (
                    <tfoot style={{ position: 'sticky', bottom: 0, background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
                      <tr style={{ borderTop: '2px solid #1e40af' }}>
                        <td colSpan="5" style={{ padding: '8px 12px', fontWeight: 700, textAlign: 'right', color: '#374151' }}>
                          Closing Balance:
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          textAlign: 'right',
                          fontWeight: 700,
                          color: '#fff',
                          backgroundColor: (() => {
                            // Find the closing balance entry from the ledger data
                            const closingBalanceEntry = ledgerData.find(ledger =>
                              (ledger.description1 || '').toLowerCase().includes('closing balance')
                            );

                            if (closingBalanceEntry) {
                              const debitAmount = parseFloat(closingBalanceEntry.debit) || 0;
                              const creditAmount = parseFloat(closingBalanceEntry.credit) || 0;
                              const balance = creditAmount - debitAmount;
                              return balance >= 0 ? '#059669' : '#dc2626';
                            }
                            return '#6b7280';
                          })()
                        }}>
                          {(() => {
                            // Find the closing balance entry from the ledger data
                            const closingBalanceEntry = ledgerData.find(ledger =>
                              (ledger.description1 || '').toLowerCase().includes('closing balance')
                            );

                            if (closingBalanceEntry) {
                              const debitAmount = parseFloat(closingBalanceEntry.debit) || 0;
                              const creditAmount = parseFloat(closingBalanceEntry.credit) || 0;
                              const balance = creditAmount - debitAmount;
                              return `₹${Math.abs(balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${balance < 0 ? '(Dr)' : '(Cr)'}`;
                            }
                            return 'No closing balance found';
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1, borderTop: '1px solid #d1d5db', bgcolor: '#f8fafc' }}>
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', mr: 'auto' }}>
            {ledgerData.length > 0 && `${ledgerData.length} entries found`}
          </Typography>
          <Button onClick={handleCloseLedgerModal} variant="outlined" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SaleModal;


